/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * CDF Template API
 * OpenAPI-first contract for the CDF template.

Workflow:
  1. Edit this spec FIRST for any API change.
  2. Run `pnpm gen:api` to regenerate the React Query client
     (lib/api-client-react) and Zod schemas (lib/api-zod).
  3. Implement/adjust the Fastify routes in artifacts/api-server
     to match, validating with the generated Zod schemas.

 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { customFetch } from '../mutator';
export type HealthStatus = typeof HealthStatus[keyof typeof HealthStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HealthStatus = {
  ok: 'ok',
} as const;

export type HealthDatabase = typeof HealthDatabase[keyof typeof HealthDatabase];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HealthDatabase = {
  connected: 'connected',
  not_configured: 'not_configured',
} as const;

export type HealthPlugins = {
  database: boolean;
  storage: boolean;
  ai: boolean;
  auth: boolean;
};

export interface Health {
  status: HealthStatus;
  uptimeSeconds: number;
  timestamp: string;
  database: HealthDatabase;
  plugins: HealthPlugins;
}

export type PluginStatusValue = typeof PluginStatusValue[keyof typeof PluginStatusValue];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PluginStatusValue = {
  ready: 'ready',
  disabled: 'disabled',
  misconfigured: 'misconfigured',
} as const;

export type DatabasePluginCapabilities = {
  persistentStorage: boolean;
  appUsers: boolean;
};

export interface DatabasePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: DatabasePluginCapabilities;
}

export type StoragePluginCapabilities = {
  fileUpload: boolean;
  fileDownload: boolean;
};

export interface StoragePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: StoragePluginCapabilities;
}

export type AiPluginCapabilities = {
  llm: boolean;
  embeddings: boolean;
  semanticSearch: boolean;
};

export interface AiPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: AiPluginCapabilities;
}

/**
 * Public MSAL client configuration (no secrets).
 */
export interface AuthClientConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLoginRedirect: string;
  scopes: string[];
}

export type AuthPluginProvider = typeof AuthPluginProvider[keyof typeof AuthPluginProvider];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AuthPluginProvider = {
  microsoft_entra_workforce: 'microsoft_entra_workforce',
} as const;

export type AuthPluginCapabilities = {
  login: boolean;
  protectedRoutes: boolean;
  persistentUsers: boolean;
};

export interface AuthPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  provider?: AuthPluginProvider;
  mode?: string;
  capabilities: AuthPluginCapabilities;
  config?: AuthClientConfig;
}

export interface PluginsStatus {
  database: DatabasePlugin;
  storage: StoragePlugin;
  ai: AiPlugin;
  auth: AuthPlugin;
}

/**
 * The **effective** staff role for this request, computed from the
user's real role and the X-Acting-Role header. When the user is
acting as a lower role (or "community"), this reflects the
downgraded role. Null for non-staff users.

 * @nullable
 */
export type CurrentUserStaffRole = typeof CurrentUserStaffRole[keyof typeof CurrentUserStaffRole] | null;


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CurrentUserStaffRole = {
  staff: 'staff',
  admin: 'admin',
  super_admin: 'super_admin',
  community: 'community',
} as const;

/**
 * The actual staff_users.role — never affected by the acting-role
header. The frontend uses this to render switcher options at or
below the user's real access level. Null for non-staff users.

 * @nullable
 */
export type CurrentUserRealStaffRole = typeof CurrentUserRealStaffRole[keyof typeof CurrentUserRealStaffRole] | null;


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CurrentUserRealStaffRole = {
  staff: 'staff',
  admin: 'admin',
  super_admin: 'super_admin',
} as const;

export interface CurrentUser {
  /** @nullable */
  appUserId?: string | null;
  entraObjectId: string;
  entraTenantId: string;
  /** @nullable */
  email?: string | null;
  displayName: string;
  /** True when the user is synced to the app_users table. */
  persistent: boolean;
  /**
   * The **effective** staff role for this request, computed from the
user's real role and the X-Acting-Role header. When the user is
acting as a lower role (or "community"), this reflects the
downgraded role. Null for non-staff users.

   * @nullable
   */
  staffRole?: CurrentUserStaffRole;
  /**
   * The actual staff_users.role — never affected by the acting-role
header. The frontend uses this to render switcher options at or
below the user's real access level. Null for non-staff users.

   * @nullable
   */
  realStaffRole?: CurrentUserRealStaffRole;
  /**
   * The staff user's home tenant ID (their own council).
   * @nullable
   */
  homeTenantId?: string | null;
}

export interface ChatRequest {
  /**
   * @minLength 1
   * @maxLength 4000
   */
  message: string;
  conversationId?: string;
}

export interface ChatResponse {
  reply: string;
  conversationId: string;
}

export interface StoredFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /** @nullable */
  ownerDisplayName?: string | null;
}

export interface FileList {
  files: StoredFile[];
  /** @minimum 0 */
  total: number;
}

export interface DashboardStats {
  totalItems: number;
  activeItems: number;
  draftItems: number;
  archivedItems: number;
  createdThisWeek: number;
}

export interface TimeseriesPoint {
  date: string;
  created: number;
  completed: number;
}

export interface Timeseries {
  days: number;
  points: TimeseriesPoint[];
}

export type ItemStatus = typeof ItemStatus[keyof typeof ItemStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ItemStatus = {
  draft: 'draft',
  active: 'active',
  archived: 'archived',
} as const;

export interface Item {
  id: string;
  name: string;
  /** @nullable */
  description: string | null;
  status: ItemStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ItemList {
  items: Item[];
  /** @minimum 0 */
  total: number;
}

export interface ItemCreate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /** @nullable */
  description?: string | null;
  status?: ItemStatus;
}

export interface ItemUpdate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /** @nullable */
  description?: string | null;
  status?: ItemStatus;
}

export interface Error {
  error: string;
  code?: string;
}

export type TenantType = typeof TenantType[keyof typeof TenantType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TenantType = {
  council: 'council',
  service_provider: 'service_provider',
} as const;

export interface Tenant {
  id?: string;
  type?: TenantType;
  name?: string;
  slug?: string;
  /** @nullable */
  contactEmail?: string | null;
  /** @nullable */
  contactPhone?: string | null;
  brandingColor?: string;
}

export interface TenantList {
  tenants?: Tenant[];
}

export type RegionFeatureType = typeof RegionFeatureType[keyof typeof RegionFeatureType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RegionFeatureType = {
  Feature: 'Feature',
} as const;

export type RegionFeatureGeometry = { [key: string]: unknown };

export type RegionFeatureProperties = {
  id?: string;
  tenantId?: string;
  name?: string;
  slug?: string;
};

export interface RegionFeature {
  type?: RegionFeatureType;
  geometry?: RegionFeatureGeometry;
  properties?: RegionFeatureProperties;
}

export type RegionFeatureCollectionType = typeof RegionFeatureCollectionType[keyof typeof RegionFeatureCollectionType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RegionFeatureCollectionType = {
  FeatureCollection: 'FeatureCollection',
} as const;

export interface RegionFeatureCollection {
  type?: RegionFeatureCollectionType;
  features?: RegionFeature[];
}

export interface AssetType {
  id?: string;
  name?: string;
  slug?: string;
  icon?: string;
  defaultOwnerType?: string;
}

export interface AssetTypeList {
  assetTypes?: AssetType[];
}

export interface Category {
  id?: string;
  name?: string;
  slug?: string;
  /** @nullable */
  assetTypeId?: string | null;
  /** @nullable */
  assetTypeName?: string | null;
  /** @nullable */
  assetTypeSlug?: string | null;
  /** @nullable */
  assetTypeIcon?: string | null;
  /** Purpose slugs this category belongs to; empty means all purposes. */
  contexts?: string[];
}

export interface CategoryList {
  categories?: Category[];
}

export type AssetCondition = typeof AssetCondition[keyof typeof AssetCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AssetCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export interface Asset {
  id?: string;
  assetTypeId?: string;
  owningTenantId?: string;
  /** @nullable */
  regionId?: string | null;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  address?: string | null;
  condition?: AssetCondition;
  /** @nullable */
  externalSource?: string | null;
  /** @nullable */
  externalId?: string | null;
  /** @nullable */
  assetTypeName?: string | null;
  /** @nullable */
  assetTypeSlug?: string | null;
  /** @nullable */
  assetTypeIcon?: string | null;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  tenantSlug?: string | null;
}

export interface AssetList {
  assets?: Asset[];
}

export type ReportSummaryStatusCountsItemStatus = typeof ReportSummaryStatusCountsItemStatus[keyof typeof ReportSummaryStatusCountsItemStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ReportSummaryStatusCountsItemStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export type ReportSummaryStatusCountsItem = {
  status?: ReportSummaryStatusCountsItemStatus;
  count?: number;
};

export interface ReportSummary {
  statusCounts?: ReportSummaryStatusCountsItem[];
}

/**
 * Structured EXIF metadata extracted from an uploaded photo.
All fields except `hasGps` are nullable — images without EXIF or
GPS data return clean nulls rather than raising errors.

 */
export interface PhotoMetadata {
  /** Whether usable GPS coordinates were extracted. */
  hasGps: boolean;
  /**
   * Decimal-degree latitude (signed, WGS84).
   * @nullable
   */
  latitude: number | null;
  /**
   * Decimal-degree longitude (signed, WGS84).
   * @nullable
   */
  longitude: number | null;
  /**
   * Altitude in meters above sea level.
   * @nullable
   */
  altitude: number | null;
  /**
   * Capture date/time as stored in EXIF (typically "YYYY:MM:DD HH:MM:SS").
   * @nullable
   */
  dateTimeOriginal: string | null;
  /** @nullable */
  cameraMake: string | null;
  /** @nullable */
  cameraModel: string | null;
  /**
   * Image width in pixels (from EXIF, may differ from actual decoded size).
   * @nullable
   */
  width: number | null;
  /**
   * Image height in pixels (from EXIF, may differ from actual decoded size).
   * @nullable
   */
  height: number | null;
  /**
   * EXIF orientation tag value (1 = normal, 3 = 180°, 6 = 90° CW, 8 = 270° CW).
   * @minimum 1
   * @maximum 8
   * @nullable
   */
  orientation: number | null;
}

export interface PhotoUploadResult {
  /** Storage blob key to pass when creating the report. */
  blobKey: string;
  metadata: PhotoMetadata;
}

export type CreateReportRequestSeverity = typeof CreateReportRequestSeverity[keyof typeof CreateReportRequestSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateReportRequestSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export interface CreateReportRequest {
  /** @maxItems 5 */
  photoBlobKeys?: string[];
  latitude: number;
  longitude: number;
  /** @nullable */
  addressText?: string | null;
  categoryId: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  reporterName?: string | null;
  /** @nullable */
  reporterEmail?: string | null;
  /** @nullable */
  reporterPhone?: string | null;
  contactPrivacy?: boolean;
  severity?: CreateReportRequestSeverity;
  aiDetection?: AiDetection;
  /** @nullable */
  matchedAssetId?: string | null;
  /**
   * Custom issue-type label (3–60 chars). Only accepted when
categoryId is the fallback "Other" category. Stored on the
report and queued as a pending suggestion for staff review.

   * @nullable
   */
  customTypeLabel?: string | null;
  /**
   * Verified event/twin slug. Event reports are excluded from civic workflows.
   * @nullable
   * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*
   */
  eventSlug?: string | null;
  /**
   * Attendee-facing event name shown with the report.
   * @maxLength 160
   * @nullable
   */
  eventName?: string | null;
  /** True for treasure-hunt demo reports. These are excluded from
staff report queues, dashboard KPIs, notifications, and
auto-routing.
 */
  isDemo?: boolean;
}

export interface CreateReportResult {
  trackingToken: string;
  trackingUrl: string;
  reportId: string;
  /** True when the report was attached to the signed-in user's account at submission time. */
  attachedToAccount: boolean;
}

export interface ClaimReportResult {
  /** True when the report was attached to the caller (newly or already owned). */
  claimed: boolean;
  trackingUrl: string;
  /** True when the report was already owned by the caller (idempotent claim). */
  alreadyOwned?: boolean;
}

export type MyReportStatus = typeof MyReportStatus[keyof typeof MyReportStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MyReportStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export interface MyReport {
  id: string;
  trackingToken: string;
  /** @nullable */
  categoryName?: string | null;
  /** @nullable */
  customTypeLabel?: string | null;
  status: MyReportStatus;
  severity: string;
  upvoteCount: number;
  /** @nullable */
  photoUrl?: string | null;
  /** @nullable */
  addressText?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NearbyReport {
  id?: string;
  trackingToken?: string;
  categoryId?: string;
  categoryName?: string;
  categorySlug?: string;
  /** @nullable */
  photoUrl?: string | null;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  addressText?: string | null;
  status?: string;
  upvoteCount?: number;
  distance?: number;
  createdAt?: string;
  /** @nullable */
  description?: string | null;
  /**
   * When present, display as "Other — {label}".
   * @nullable
   */
  customTypeLabel?: string | null;
}

export interface NearbyReportList {
  reports?: NearbyReport[];
}

export interface UpvoteResult {
  upvoteCount?: number;
  trackingUrl?: string;
}

/**
 * @nullable
 */
export type AiDetection = {
  /** @nullable */
  assetType?: string | null;
  /** @nullable */
  problemType?: string | null;
  /** @nullable */
  severity?: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  confidence?: number | null;
} | null;

export type DetectionResultSeverity = typeof DetectionResultSeverity[keyof typeof DetectionResultSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const DetectionResultSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

/**
 * @nullable
 */
export type DetectionResult = {
  assetTypeSlug?: string;
  assetTypeName?: string;
  problemType?: string;
  severity?: DetectionResultSeverity;
  description?: string;
  confidence?: number;
  /** @nullable */
  categoryId?: string | null;
  /** @nullable */
  categoryName?: string | null;
} | null;

/**
 * @nullable
 */
export type MatchedAsset = {
  assetId?: string;
  assetTypeName?: string;
  ownerTenantName?: string;
  ownerTenantType?: string;
  distance?: number;
} | null;

export interface DetectResponse {
  detection?: DetectionResult;
  matchedAsset?: MatchedAsset;
}

export interface MatchAssetResponse {
  matchedAsset?: MatchedAsset;
}

/**
 * @nullable
 */
export type MatchedAssetInfo = {
  /** @nullable */
  ownerTenantName?: string | null;
  /** @nullable */
  ownerTenantType?: string | null;
  /** @nullable */
  distance?: number | null;
} | null;

export interface NudgeResult {
  nudged?: boolean;
  message?: string;
}

export interface StatusTimelineEntry {
  status?: string;
  /** @nullable */
  note?: string | null;
  createdAt?: string;
}

export interface AddReportCommentRequest {
  /**
   * The comment text.
   * @minLength 1
   * @maxLength 2000
   */
  body: string;
}

export type ReportCommentResultAuthorType = typeof ReportCommentResultAuthorType[keyof typeof ReportCommentResultAuthorType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ReportCommentResultAuthorType = {
  reporter: 'reporter',
} as const;

export interface ReportCommentResult {
  id?: string;
  body?: string;
  /** @nullable */
  authorDisplayName?: string | null;
  authorType?: ReportCommentResultAuthorType;
  createdAt?: string;
}

/**
 * Whether this comment was authored by council staff or the reporter.
 * @nullable
 */
export type PublicCommentAuthorType = typeof PublicCommentAuthorType[keyof typeof PublicCommentAuthorType] | null;


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PublicCommentAuthorType = {
  staff: 'staff',
  reporter: 'reporter',
} as const;

export interface PublicComment {
  id?: string;
  body?: string;
  /**
   * Display name of the commenter (set for reporter comments).
   * @nullable
   */
  authorDisplayName?: string | null;
  /**
   * Whether this comment was authored by council staff or the reporter.
   * @nullable
   */
  authorType?: PublicCommentAuthorType;
  createdAt?: string;
}

export interface ReportTracking {
  id?: string;
  trackingToken?: string;
  /** @nullable */
  photoUrl?: string | null;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  addressText?: string | null;
  categoryName?: string;
  categorySlug?: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  customTypeLabel?: string | null;
  status?: string;
  severity?: string;
  upvoteCount?: number;
  aiDetection?: AiDetection;
  /** @nullable */
  matchedAsset?: MatchedAssetInfo;
  createdAt?: string;
  updatedAt?: string;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  tenantBrandingColor?: string | null;
  /** Whether the authenticated caller owns this report (false when not signed in). */
  isOwner?: boolean;
  timeline?: StatusTimelineEntry[];
  comments?: PublicComment[];
}

export type IssueFeatureCollectionType = typeof IssueFeatureCollectionType[keyof typeof IssueFeatureCollectionType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const IssueFeatureCollectionType = {
  FeatureCollection: 'FeatureCollection',
} as const;

export interface IssueFeatureCollection {
  type: IssueFeatureCollectionType;
  features: IssueFeature[];
}

export type IssueFeatureType = typeof IssueFeatureType[keyof typeof IssueFeatureType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const IssueFeatureType = {
  Feature: 'Feature',
} as const;

export type IssueFeatureGeometryType = typeof IssueFeatureGeometryType[keyof typeof IssueFeatureGeometryType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const IssueFeatureGeometryType = {
  Point: 'Point',
} as const;

export type IssueFeatureGeometry = {
  type: IssueFeatureGeometryType;
  /**
   * @minItems 2
   * @maxItems 2
   */
  coordinates: number[];
};

export type IssueFeaturePropertiesStatus = typeof IssueFeaturePropertiesStatus[keyof typeof IssueFeaturePropertiesStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const IssueFeaturePropertiesStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export type IssueFeaturePropertiesSeverity = typeof IssueFeaturePropertiesSeverity[keyof typeof IssueFeaturePropertiesSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const IssueFeaturePropertiesSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export type IssueFeatureProperties = {
  id: string;
  trackingToken: string;
  status: IssueFeaturePropertiesStatus;
  severity: IssueFeaturePropertiesSeverity;
  upvoteCount?: number;
  /** @nullable */
  categoryName?: string | null;
  /** @nullable */
  categorySlug?: string | null;
  /** @nullable */
  addressText?: string | null;
  hasPhoto: boolean;
  /** @nullable */
  photoUrl?: string | null;
  createdAt: string;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  tenantId?: string | null;
};

export interface IssueFeature {
  type: IssueFeatureType;
  geometry: IssueFeatureGeometry;
  properties: IssueFeatureProperties;
}

export type AssetFeatureCollectionType = typeof AssetFeatureCollectionType[keyof typeof AssetFeatureCollectionType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AssetFeatureCollectionType = {
  FeatureCollection: 'FeatureCollection',
} as const;

export interface AssetFeatureCollection {
  type: AssetFeatureCollectionType;
  features: AssetFeature[];
}

export type AssetFeatureType = typeof AssetFeatureType[keyof typeof AssetFeatureType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AssetFeatureType = {
  Feature: 'Feature',
} as const;

export type AssetFeatureGeometryType = typeof AssetFeatureGeometryType[keyof typeof AssetFeatureGeometryType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AssetFeatureGeometryType = {
  Point: 'Point',
} as const;

export type AssetFeatureGeometry = {
  type: AssetFeatureGeometryType;
  /**
   * @minItems 2
   * @maxItems 2
   */
  coordinates: number[];
};

export type AssetFeatureProperties = {
  id: string;
  /** @nullable */
  assetTypeName: string | null;
  /** @nullable */
  assetTypeSlug?: string | null;
  /** @nullable */
  assetTypeIcon?: string | null;
  /** @nullable */
  address?: string | null;
  /** @nullable */
  condition: string | null;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  tenantType?: string | null;
  /** @nullable */
  owningTenantId?: string | null;
  /** @nullable */
  regionId?: string | null;
  openIssueCount: number;
};

export interface AssetFeature {
  type: AssetFeatureType;
  geometry: AssetFeatureGeometry;
  properties: AssetFeatureProperties;
}

/**
 * GeoJSON polygon boundary
 * @nullable
 */
export type RegionSummaryBoundary = { [key: string]: unknown } | null;

export interface RegionSummary {
  id: string;
  name: string;
  slug?: string;
  /** @nullable */
  tenantId?: string | null;
  /** @nullable */
  tenantName?: string | null;
  /**
   * GeoJSON polygon boundary
   * @nullable
   */
  boundary?: RegionSummaryBoundary;
  assetCount: number;
  openIssueCount: number;
  totalIssueCount: number;
}

/**
 * GeoJSON Polygon building footprint
 */
export type TwinBuildingFootprint = { [key: string]: unknown };

export type TwinBuildingBuildingType = typeof TwinBuildingBuildingType[keyof typeof TwinBuildingBuildingType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinBuildingBuildingType = {
  residential: 'residential',
  commercial: 'commercial',
  mixed_use: 'mixed_use',
  civic: 'civic',
} as const;

export interface TwinBuilding {
  /** GeoJSON Polygon building footprint */
  footprint: TwinBuildingFootprint;
  /** Building height in meters */
  height: number;
  buildingType: TwinBuildingBuildingType;
}

/**
 * GeoJSON LineString road segment
 */
export type TwinRoadLine = { [key: string]: unknown };

export type TwinRoadRoadType = typeof TwinRoadRoadType[keyof typeof TwinRoadRoadType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinRoadRoadType = {
  arterial: 'arterial',
  residential: 'residential',
  lane: 'lane',
} as const;

export interface TwinRoad {
  /** GeoJSON LineString road segment */
  line: TwinRoadLine;
  roadType: TwinRoadRoadType;
}

export type TwinAssetCondition = typeof TwinAssetCondition[keyof typeof TwinAssetCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinAssetCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export type TwinAssetTenantType = typeof TwinAssetTenantType[keyof typeof TwinAssetTenantType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinAssetTenantType = {
  council: 'council',
  service_provider: 'service_provider',
} as const;

export interface TwinAsset {
  id: string;
  latitude: number;
  longitude: number;
  address?: string;
  condition: TwinAssetCondition;
  assetTypeName: string;
  assetTypeSlug: string;
  assetTypeIcon?: string;
  owningTenantId?: string;
  tenantName: string;
  tenantType: TwinAssetTenantType;
  tenantBrandingColor?: string;
  openIssueCount: number;
}

export type TwinIssueStatus = typeof TwinIssueStatus[keyof typeof TwinIssueStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinIssueStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export type TwinIssueSeverity = typeof TwinIssueSeverity[keyof typeof TwinIssueSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinIssueSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export interface TwinIssue {
  id: string;
  trackingToken: string;
  latitude: number;
  longitude: number;
  status: TwinIssueStatus;
  severity: TwinIssueSeverity;
  upvoteCount?: number;
  categoryName: string;
  categorySlug: string;
  addressText?: string;
  hasPhoto?: boolean;
  /** @nullable */
  photoUrl?: string | null;
  createdAt: string;
  /** @nullable */
  matchedAssetId?: string | null;
  tenantName: string;
  tenantId?: string;
}

/**
 * GeoJSON polygon boundary
 */
export type TwinRegionBoundary = { [key: string]: unknown };

export interface TwinRegion {
  id: string;
  name: string;
  slug: string;
  tenantId: string;
  tenantName: string;
  tenantBrandingColor?: string;
  /** GeoJSON polygon boundary */
  boundary: TwinRegionBoundary;
  centerLng: number;
  centerLat: number;
}

export interface TwinScene {
  region: TwinRegion;
  buildings: TwinBuilding[];
  roads: TwinRoad[];
  assets: TwinAsset[];
  issues: TwinIssue[];
}

/**
 * Include either latitude/longitude for civic twins or floorX/floorZ for conference twins.
 */
export interface TwinPresenceInput {
  /**
   * @minLength 1
   * @maxLength 128
   */
  userId: string;
  /**
   * @minLength 1
   * @maxLength 100
   */
  sceneId: string;
  /**
   * @minLength 1
   * @maxLength 32
   */
  displayName: string;
  /** @pattern ^#[0-9a-fA-F]{6}$ */
  avatarColor: string;
  /**
   * @minimum -90
   * @maximum 90
   * @nullable
   */
  latitude?: number | null;
  /**
   * @minimum -180
   * @maximum 180
   * @nullable
   */
  longitude?: number | null;
  /** @nullable */
  floorX?: number | null;
  /** @nullable */
  floorZ?: number | null;
  /**
   * @minimum 0
   * @maximum 100000
   * @nullable
   */
  accuracyMeters?: number | null;
  /**
   * @minimum 1
   * @maximum 300
   */
  updateIntervalSec?: number;
}

export type TwinPresenceAllOf = {
  /** Unix timestamp in milliseconds */
  lastHeartbeat: number;
  /** Unix timestamp in milliseconds */
  joinedAt: number;
};

export type TwinPresence = TwinPresenceInput & TwinPresenceAllOf;

export type TwinPresenceWriteResponseAllOf = {
  /** Secret token required for this participant's future updates and leave request. */
  presenceToken: string;
};

export type TwinPresenceWriteResponse = TwinPresence & TwinPresenceWriteResponseAllOf;

export interface TwinPresenceList {
  sceneId: string;
  participants: TwinPresence[];
  /** @minimum 0 */
  count: number;
}

export interface TwinPresenceRemoval {
  removed: boolean;
  userId: string;
  sceneId: string;
}

export type TwinCatalogItemEntityType = typeof TwinCatalogItemEntityType[keyof typeof TwinCatalogItemEntityType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinCatalogItemEntityType = {
  event: 'event',
  location: 'location',
} as const;

export interface TwinCatalogItem {
  id: string;
  entityType: TwinCatalogItemEntityType;
  entitySlug: string;
  /** @nullable */
  entityId?: string | null;
  title: string;
  summary: string;
  scheduleStatus: string;
}

export type TwinCatalogResultPagination = {
  limit: number;
  offset: number;
  hasMore: boolean;
};

export interface TwinCatalogResult {
  items: TwinCatalogItem[];
  pagination: TwinCatalogResultPagination;
}

/**
 * Versioned event-specific 3D scene configuration
 */
export type EventTwinSceneScene = { [key: string]: unknown };

export interface EventTwinScene {
  twin: TwinCatalogItem;
  /** Versioned event-specific 3D scene configuration */
  scene: EventTwinSceneScene;
  scheduleStatus: string;
}

export type TwinWallAnchorWallId = typeof TwinWallAnchorWallId[keyof typeof TwinWallAnchorWallId];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TwinWallAnchorWallId = {
  north: 'north',
  south: 'south',
  east: 'east',
  west: 'west',
} as const;

export interface TwinWallAnchor {
  roomId: string;
  wallId: TwinWallAnchorWallId;
  x: number;
  y: number;
  z: number;
  rotationY: number;
}

export type EventTwinItemType = typeof EventTwinItemType[keyof typeof EventTwinItemType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EventTwinItemType = {
  event_report: 'event_report',
  hunt_capture: 'hunt_capture',
} as const;

export interface EventTwinItem {
  id: string;
  sourceId: string;
  type: EventTwinItemType;
  title: string;
  /** @nullable */
  description: string | null;
  photoBlobKeys: string[];
  /** @nullable */
  photoUrl: string | null;
  /** @minimum 0 */
  supportCount: number;
  /**
   * GPS latitude captured with an event report.
   * @nullable
   */
  latitude?: number | null;
  /**
   * GPS longitude captured with an event report.
   * @nullable
   */
  longitude?: number | null;
  wallAnchor: TwinWallAnchor;
  createdAt: string;
}

export interface EventTwinItemSupportResult {
  itemId: string;
  /** @minimum 0 */
  supportCount: number;
}

export type EventTwinItemListPagination = {
  limit: number;
  offset: number;
  hasMore: boolean;
};

export interface EventTwinItemList {
  items: EventTwinItem[];
  pagination: EventTwinItemListPagination;
}

export type CreateEventReportRequestWallId = typeof CreateEventReportRequestWallId[keyof typeof CreateEventReportRequestWallId];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateEventReportRequestWallId = {
  north: 'north',
  south: 'south',
  east: 'east',
  west: 'west',
} as const;

export type CreateEventReportRequestSeverity = typeof CreateEventReportRequestSeverity[keyof typeof CreateEventReportRequestSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateEventReportRequestSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export interface CreateEventReportRequest {
  /**
   * @minLength 3
   * @maxLength 120
   */
  title: string;
  /**
   * @minLength 3
   * @maxLength 2000
   */
  description: string;
  categoryId: string;
  /**
   * @minItems 1
   * @maxItems 5
   */
  photoBlobKeys: string[];
  roomId: string;
  wallId: CreateEventReportRequestWallId;
  severity?: CreateEventReportRequestSeverity;
}

export type CreateEventReportResponseReport = {
  id: string;
  trackingToken: string;
  title: string;
  twinLocation: TwinWallAnchor;
};

export interface CreateEventReportResponse {
  report: CreateEventReportResponseReport;
}

export interface LocationTwinScene {
  twin: TwinCatalogItem;
  scene: TwinScene;
}

export type NearbyAssetOpenIssueStatus = typeof NearbyAssetOpenIssueStatus[keyof typeof NearbyAssetOpenIssueStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NearbyAssetOpenIssueStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

/**
 * @nullable
 */
export type NearbyAssetOpenIssueSeverity = typeof NearbyAssetOpenIssueSeverity[keyof typeof NearbyAssetOpenIssueSeverity] | null;


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NearbyAssetOpenIssueSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export interface NearbyAssetOpenIssue {
  id: string;
  trackingToken: string;
  status: NearbyAssetOpenIssueStatus;
  /** @nullable */
  severity?: NearbyAssetOpenIssueSeverity;
  /** @nullable */
  categoryName?: string | null;
  /** @nullable */
  categorySlug?: string | null;
}

export type NearbyAssetTenantType = typeof NearbyAssetTenantType[keyof typeof NearbyAssetTenantType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NearbyAssetTenantType = {
  council: 'council',
  provider: 'provider',
} as const;

export interface NearbyAsset {
  id: string;
  latitude: number;
  longitude: number;
  /** Distance from the query point in metres */
  distance: number;
  /** @nullable */
  address?: string | null;
  /** @nullable */
  condition?: string | null;
  assetTypeName: string;
  assetTypeSlug: string;
  assetTypeIcon: string;
  owningTenantId: string;
  tenantName: string;
  tenantType: NearbyAssetTenantType;
  /** @nullable */
  tenantBrandingColor?: string | null;
  /** @nullable */
  regionId?: string | null;
  /** Compass bearing from query point to asset in degrees */
  bearing: number;
  openIssues: NearbyAssetOpenIssue[];
}

export interface NearbyAssetsResponse {
  assets: NearbyAsset[];
}

export type WorkspaceKpiByStatus = {[key: string]: number};

export type WorkspaceKpiByCategoryItem = {
  name?: string;
  count?: number;
};

export interface WorkspaceKpi {
  totalReports?: number;
  openCount?: number;
  closedCount?: number;
  byStatus?: WorkspaceKpiByStatus;
  byCategory?: WorkspaceKpiByCategoryItem[];
  /** @nullable */
  medianTimeToResolveMs?: number | null;
  nudgeCount?: number;
}

export type WorkspaceStaffListStaffItemRole = typeof WorkspaceStaffListStaffItemRole[keyof typeof WorkspaceStaffListStaffItemRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WorkspaceStaffListStaffItemRole = {
  staff: 'staff',
  admin: 'admin',
} as const;

export type WorkspaceStaffListStaffItem = {
  id?: string;
  displayName?: string;
  email?: string;
  role?: WorkspaceStaffListStaffItemRole;
};

export interface WorkspaceStaffList {
  staff?: WorkspaceStaffListStaffItem[];
}

export type SwitchableCouncilListCouncilsItem = {
  id: string;
  name: string;
  slug: string;
  brandingColor: string;
  openReportCount: number;
};

export interface SwitchableCouncilList {
  councils: SwitchableCouncilListCouncilsItem[];
}

/**
 * @nullable
 */
export type WorkspaceReportListItemAiDetection = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type WorkspaceReportListItemAssignedTo = {
  staffId?: string;
  staffName?: string;
} | null;

export interface WorkspaceReportListItem {
  id?: string;
  trackingToken?: string;
  status?: string;
  severity?: string;
  categoryId?: string;
  categoryName?: string;
  categorySlug?: string;
  description?: string;
  /** @nullable */
  customTypeLabel?: string | null;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  addressText?: string | null;
  upvoteCount?: number;
  /** @nullable */
  aiDetection?: WorkspaceReportListItemAiDetection;
  /** @nullable */
  matchedAssetId?: string | null;
  photoBlobKeys?: string[];
  /** @nullable */
  photoUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** @nullable */
  assignedTo?: WorkspaceReportListItemAssignedTo;
  isPriority?: boolean;
}

export interface WorkspaceReportList {
  reports?: WorkspaceReportListItem[];
  page?: number;
  limit?: number;
  total?: number;
}

/**
 * @nullable
 */
export type WorkspaceReportDetailAiDetection = { [key: string]: unknown } | null;

export type WorkspaceReportDetailTimelineItem = {
  id?: string;
  status?: string;
  /** @nullable */
  note?: string | null;
  /** @nullable */
  changedByStaffId?: string | null;
  /** @nullable */
  staffName?: string | null;
  createdAt?: string;
};

export type WorkspaceReportDetailCommentsItem = {
  id?: string;
  body?: string;
  isInternal?: boolean;
  /** @nullable */
  authorName?: string | null;
  /** @nullable */
  authorUserId?: string | null;
  /** @nullable */
  authorDisplayName?: string | null;
  createdAt?: string;
};

export type WorkspaceReportDetailAssignmentsItem = {
  id?: string;
  assignedToStaffId?: string;
  /** @nullable */
  staffName?: string | null;
  staffRole?: string;
  createdAt?: string;
};

/**
 * @nullable
 */
export type WorkspaceReportDetailMatchedAsset = {
  id?: string;
  typeName?: string;
  typeSlug?: string;
  condition?: string;
  ownerTenantName?: string;
  ownerTenantType?: string;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  address?: string | null;
} | null;

export type WorkspaceReportDetailNotificationsItem = {
  id?: string;
  recipientEmail?: string;
  subject?: string;
  status?: string;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  error?: string | null;
  createdAt?: string;
};

export interface WorkspaceReportDetail {
  id?: string;
  trackingToken?: string;
  /** @nullable */
  reporterName?: string | null;
  /** @nullable */
  reporterEmail?: string | null;
  /** @nullable */
  reporterPhone?: string | null;
  contactPrivacy?: boolean;
  photoBlobKeys?: string[];
  /** @nullable */
  photoUrl?: string | null;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  addressText?: string | null;
  categoryId?: string;
  categoryName?: string;
  categorySlug?: string;
  description?: string;
  /** @nullable */
  customTypeLabel?: string | null;
  status?: string;
  severity?: string;
  upvoteCount?: number;
  /** @nullable */
  aiDetection?: WorkspaceReportDetailAiDetection;
  /** @nullable */
  matchedAssetId?: string | null;
  owningTenantId?: string;
  /** @nullable */
  regionId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  timeline?: WorkspaceReportDetailTimelineItem[];
  comments?: WorkspaceReportDetailCommentsItem[];
  assignments?: WorkspaceReportDetailAssignmentsItem[];
  /** @nullable */
  matchedAsset?: WorkspaceReportDetailMatchedAsset;
  notifications?: WorkspaceReportDetailNotificationsItem[];
}

export type TransitionRequestNewStatus = typeof TransitionRequestNewStatus[keyof typeof TransitionRequestNewStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TransitionRequestNewStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export interface TransitionRequest {
  newStatus: TransitionRequestNewStatus;
  /**
   * @minLength 1
   * @maxLength 2000
   */
  note: string;
  canonicalReportId?: string;
}

export interface CommentRequest {
  /**
   * @minLength 1
   * @maxLength 5000
   */
  body: string;
  isInternal?: boolean;
}

export interface CommentResponse {
  id?: string;
  body?: string;
  isInternal?: boolean;
  authorName?: string;
  createdAt?: string;
}

export type WorkspaceAssetListItemCondition = typeof WorkspaceAssetListItemCondition[keyof typeof WorkspaceAssetListItemCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WorkspaceAssetListItemCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export interface WorkspaceAssetListItem {
  id?: string;
  assetTypeId?: string;
  typeName?: string;
  typeSlug?: string;
  typeIcon?: string;
  latitude?: number;
  longitude?: number;
  /** @nullable */
  address?: string | null;
  condition?: WorkspaceAssetListItemCondition;
  /** @nullable */
  regionId?: string | null;
  /** @nullable */
  externalSource?: string | null;
  /** @nullable */
  externalId?: string | null;
  /** @nullable */
  retiredAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkspaceAssetList {
  assets: WorkspaceAssetListItem[];
  page: number;
  limit: number;
  total: number;
}

export type CreateAssetRequestCondition = typeof CreateAssetRequestCondition[keyof typeof CreateAssetRequestCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateAssetRequestCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export interface CreateAssetRequest {
  assetTypeId: string;
  /**
   * @minimum -90
   * @maximum 90
   */
  latitude: number;
  /**
   * @minimum -180
   * @maximum 180
   */
  longitude: number;
  /** @maxLength 500 */
  address?: string;
  condition?: CreateAssetRequestCondition;
  regionId?: string;
  /** @maxLength 200 */
  externalSource?: string;
  /** @maxLength 200 */
  externalId?: string;
}

export type UpdateAssetRequestCondition = typeof UpdateAssetRequestCondition[keyof typeof UpdateAssetRequestCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const UpdateAssetRequestCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export interface UpdateAssetRequest {
  assetTypeId?: string;
  /**
   * @minimum -90
   * @maximum 90
   */
  latitude?: number;
  /**
   * @minimum -180
   * @maximum 180
   */
  longitude?: number;
  /** @maxLength 500 */
  address?: string;
  condition?: UpdateAssetRequestCondition;
  /** @nullable */
  regionId?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  externalSource?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  externalId?: string | null;
}

export interface CreateAssetTypeRequest {
  /** @maxLength 100 */
  name: string;
  /** @maxLength 100 */
  slug?: string;
  /** @maxLength 50 */
  icon?: string;
  /** @maxLength 50 */
  defaultOwnerType?: string;
}

export interface UpdateAssetTypeRequest {
  /** @maxLength 100 */
  name?: string;
  /** @maxLength 100 */
  slug?: string;
  /** @maxLength 50 */
  icon?: string;
  /** @maxLength 50 */
  defaultOwnerType?: string;
}

export interface WorkspaceAssetTypeListItem {
  id?: string;
  name?: string;
  slug?: string;
  icon?: string;
  defaultOwnerType?: string;
  createdAt?: string;
}

export interface WorkspaceAssetTypeList {
  assetTypes: WorkspaceAssetTypeListItem[];
}

export type WorkspaceRegionListItemBoundaryGeojson = { [key: string]: unknown };

export interface WorkspaceRegionListItem {
  id?: string;
  name?: string;
  slug?: string;
  boundaryGeojson?: WorkspaceRegionListItemBoundaryGeojson;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkspaceRegionList {
  regions: WorkspaceRegionListItem[];
}

export type ImportUploadResponseSourceType = typeof ImportUploadResponseSourceType[keyof typeof ImportUploadResponseSourceType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ImportUploadResponseSourceType = {
  geojson: 'geojson',
  csv: 'csv',
} as const;

export type ImportUploadResponseSampleRowsItem = { [key: string]: unknown };

export type ImportUploadResponseSuggestedMapping = { [key: string]: unknown };

export type ImportUploadResponseAvailableAssetTypesItem = {
  id?: string;
  name?: string;
  slug?: string;
};

export interface ImportUploadResponse {
  sessionId?: string;
  sourceType?: ImportUploadResponseSourceType;
  fileName?: string;
  totalRows?: number;
  headers?: string[];
  sampleRows?: ImportUploadResponseSampleRowsItem[];
  suggestedMapping?: ImportUploadResponseSuggestedMapping;
  availableAssetTypes?: ImportUploadResponseAvailableAssetTypesItem[];
}

export type ImportPreviewRequestMapping = { [key: string]: unknown };

export interface ImportPreviewRequest {
  sessionId: string;
  /** @maxLength 200 */
  externalSource?: string;
  mapping: ImportPreviewRequestMapping;
}

export type ImportPreviewResponseErrorsItem = {
  row?: number;
  message?: string;
};

export type ImportPreviewResponseSampleValidRowsItem = { [key: string]: unknown };

export interface ImportPreviewResponse {
  totalRows?: number;
  validRows?: number;
  errorCount?: number;
  errors?: ImportPreviewResponseErrorsItem[];
  sampleValidRows?: ImportPreviewResponseSampleValidRowsItem[];
}

export type ImportCommitResponseErrorDetailsItem = {
  row?: number;
  message?: string;
};

export interface ImportCommitResponse {
  importRunId?: string;
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  errorDetails?: ImportCommitResponseErrorDetailsItem[];
}

export interface ImportHistoryItem {
  id?: string;
  sourceType?: string;
  /** @nullable */
  externalSource?: string | null;
  /** @nullable */
  fileName?: string | null;
  status?: string;
  createdCount?: number;
  updatedCount?: number;
  skippedCount?: number;
  errorCount?: number;
  createdAt?: string;
  /** @nullable */
  completedAt?: string | null;
}

export interface ImportHistoryList {
  imports: ImportHistoryItem[];
  page: number;
  limit: number;
}

export interface GeocodeResult {
  /** Human-readable address label. */
  label: string;
  latitude: number;
  longitude: number;
}

export interface GeocodeSearchResponse {
  results: GeocodeResult[];
}

export type CategorySuggestionStatus = typeof CategorySuggestionStatus[keyof typeof CategorySuggestionStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CategorySuggestionStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

/**
 * @nullable
 */
export type CategorySuggestionReport = {
  id?: string;
  trackingToken?: string;
  /** @nullable */
  photoUrl?: string | null;
  /** @nullable */
  addressText?: string | null;
  status?: string;
  createdAt?: string;
} | null;

export interface CategorySuggestion {
  id: string;
  label: string;
  slug: string;
  /** @nullable */
  reportId?: string | null;
  status: CategorySuggestionStatus;
  /** @nullable */
  assetTypeId?: string | null;
  /** @nullable */
  approvedCategoryId?: string | null;
  /** @nullable */
  reviewedByStaffId?: string | null;
  /** @nullable */
  reviewedByStaffName?: string | null;
  createdAt: string;
  /** @nullable */
  reviewedAt?: string | null;
  /** @nullable */
  report?: CategorySuggestionReport;
}

export interface CategorySuggestionList {
  suggestions: CategorySuggestion[];
}

export type HuntSummaryStatus = typeof HuntSummaryStatus[keyof typeof HuntSummaryStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HuntSummaryStatus = {
  draft: 'draft',
  active: 'active',
  closed: 'closed',
} as const;

export interface HuntSummary {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
  status: HuntSummaryStatus;
  completionBonusPoints: number;
  createdAt: string;
  updatedAt: string;
}

export type HuntStateStatus = typeof HuntStateStatus[keyof typeof HuntStateStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HuntStateStatus = {
  draft: 'draft',
  active: 'active',
  closed: 'closed',
} as const;

export interface HuntState {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
  status: HuntStateStatus;
  completionBonusPoints: number;
  stations: PublicStation[];
  challenges: PublicChallenge[];
}

export type PublicStationKind = typeof PublicStationKind[keyof typeof PublicStationKind];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PublicStationKind = {
  qr: 'qr',
  gps: 'gps',
} as const;

/**
 * Station as returned by public endpoints (no secret code)
 */
export interface PublicStation {
  id: string;
  name: string;
  kind: PublicStationKind;
  points: number;
  sortOrder: number;
  /** @nullable */
  latitude?: number | null;
  /** @nullable */
  longitude?: number | null;
  /** @nullable */
  captureRadiusM?: number | null;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /** @nullable */
  room?: string | null;
  /** @nullable */
  presenter?: string | null;
}

export type PublicChallengeType = typeof PublicChallengeType[keyof typeof PublicChallengeType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PublicChallengeType = {
  demo_report: 'demo_report',
  ar_view: 'ar_view',
  map_find: 'map_find',
  quiz: 'quiz',
  cv_scan: 'cv_scan',
} as const;

export interface PublicChallenge {
  id: string;
  stationId: string;
  type: PublicChallengeType;
  title: string;
  /** @nullable */
  instructions?: string | null;
  points: number;
  config?: ChallengeConfig;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /** @nullable */
  room?: string | null;
  /** @nullable */
  presenter?: string | null;
  /** @nullable */
  sessionQuestion?: string | null;
}

/**
 * Type-specific parameters for a challenge.
 * @nullable
 */
export type ChallengeConfig = {
  /** For map_find challenges — the asset the participant must locate. */
  targetAssetId?: string;
  /** For quiz challenges — the question text. */
  question?: string;
  /** For quiz challenges — the answer options. */
  options?: string[];
  /** For quiz challenges — the index of the correct option. */
  correctIndex?: number;
} | null;

export interface JoinHuntRequest {
  /**
   * @minLength 1
   * @maxLength 30
   */
  nickname: string;
  /** @maxLength 30 */
  team?: string;
}

export interface JoinHuntResult {
  participantId: string;
  token: string;
  nickname: string;
  /** @nullable */
  team?: string | null;
}

/**
 * Send stationCode for QR capture; stationId+lat+lng for GPS capture.
 */
export interface CaptureRequest {
  /** QR station secret code */
  stationCode?: string;
  /** GPS station id */
  stationId?: string;
  /** Required for GPS capture */
  latitude?: number;
  /** Required for GPS capture */
  longitude?: number;
}

export interface CaptureResult {
  stationId: string;
  stationName: string;
  pointsAwarded: number;
  totalPoints: number;
}

/**
 * @nullable
 */
export type CompleteChallengeRequestEvidence = {
  demoReportId?: string;
  foundAssetId?: string;
  selectedAnswer?: number;
} | null;

export interface CompleteChallengeRequest {
  /** @nullable */
  evidence?: CompleteChallengeRequestEvidence;
}

export interface CompleteChallengeResult {
  challengeId: string;
  pointsAwarded: number;
  totalPoints: number;
}

export interface HuntProgress {
  participantId: string;
  nickname: string;
  /** @nullable */
  team?: string | null;
  totalPoints: number;
  /** @nullable */
  rank?: number | null;
  capturedStationIds: string[];
  completedChallengeIds: string[];
  stationCount: number;
  challengeCount: number;
  completionBonusAwarded: boolean;
}

export interface LeaderboardEntry {
  /** @nullable */
  participantId?: string | null;
  name: string;
  /** @nullable */
  team?: string | null;
  totalPoints: number;
  captureCount: number;
  rank: number;
}

export interface RecentCapture {
  nickname: string;
  /** @nullable */
  team?: string | null;
  stationName: string;
  capturedAt: string;
}

export interface HuntLeaderboard {
  individuals: LeaderboardEntry[];
  teams: LeaderboardEntry[];
  recentCaptures: RecentCapture[];
  participantCount: number;
}

export interface HuntListResult {
  hunts: HuntSummary[];
}

export type HuntDetailStatus = typeof HuntDetailStatus[keyof typeof HuntDetailStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HuntDetailStatus = {
  draft: 'draft',
  active: 'active',
  closed: 'closed',
} as const;

export interface HuntDetail {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
  status: HuntDetailStatus;
  completionBonusPoints: number;
  createdAt?: string;
  updatedAt?: string;
  stations: StationDetail[];
  challenges: ChallengeDetail[];
}

export type StationDetailKind = typeof StationDetailKind[keyof typeof StationDetailKind];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StationDetailKind = {
  qr: 'qr',
  gps: 'gps',
} as const;

/**
 * Station as returned by admin endpoints (includes secret code)
 */
export interface StationDetail {
  id: string;
  name: string;
  kind: StationDetailKind;
  secretCode: string;
  points: number;
  sortOrder: number;
  /** @nullable */
  latitude?: number | null;
  /** @nullable */
  longitude?: number | null;
  /** @nullable */
  captureRadiusM?: number | null;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /** @nullable */
  room?: string | null;
  /** @nullable */
  presenter?: string | null;
}

export type ChallengeDetailType = typeof ChallengeDetailType[keyof typeof ChallengeDetailType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ChallengeDetailType = {
  demo_report: 'demo_report',
  ar_view: 'ar_view',
  map_find: 'map_find',
  quiz: 'quiz',
  cv_scan: 'cv_scan',
} as const;

/**
 * @nullable
 */
export type ChallengeDetailConfig = {
  targetAssetId?: string;
  question?: string;
  options?: string[];
  correctIndex?: number;
  cvTargetId?: string;
  cvTargetLabel?: string;
} | null;

export interface ChallengeDetail {
  id: string;
  stationId: string;
  type: ChallengeDetailType;
  title: string;
  /** @nullable */
  instructions?: string | null;
  points: number;
  /** @nullable */
  config?: ChallengeDetailConfig;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /** @nullable */
  room?: string | null;
  /** @nullable */
  presenter?: string | null;
  /** @nullable */
  sessionQuestion?: string | null;
}

export interface CreateHuntRequest {
  /** @pattern ^[a-z0-9-]+$ */
  slug: string;
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  description?: string;
  /** @minimum 0 */
  completionBonusPoints?: number;
}

export interface UpdateHuntRequest {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  description?: string;
  /** @minimum 0 */
  completionBonusPoints?: number;
}

export type SetHuntStatusRequestStatus = typeof SetHuntStatusRequestStatus[keyof typeof SetHuntStatusRequestStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SetHuntStatusRequestStatus = {
  draft: 'draft',
  active: 'active',
  closed: 'closed',
} as const;

export interface SetHuntStatusRequest {
  status: SetHuntStatusRequestStatus;
}

export type CreateStationRequestKind = typeof CreateStationRequestKind[keyof typeof CreateStationRequestKind];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateStationRequestKind = {
  qr: 'qr',
  gps: 'gps',
} as const;

export interface CreateStationRequest {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  kind: CreateStationRequestKind;
  /** @minimum 0 */
  points?: number;
  sortOrder?: number;
  latitude?: number;
  longitude?: number;
  /** @minimum 1 */
  captureRadiusM?: number;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /**
   * @maxLength 120
   * @nullable
   */
  room?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  presenter?: string | null;
}

export interface UpdateStationRequest {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /** @minimum 0 */
  points?: number;
  sortOrder?: number;
  latitude?: number;
  longitude?: number;
  /** @minimum 1 */
  captureRadiusM?: number;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /**
   * @maxLength 120
   * @nullable
   */
  room?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  presenter?: string | null;
}

export type CreateChallengeRequestType = typeof CreateChallengeRequestType[keyof typeof CreateChallengeRequestType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CreateChallengeRequestType = {
  demo_report: 'demo_report',
  ar_view: 'ar_view',
  map_find: 'map_find',
  quiz: 'quiz',
  cv_scan: 'cv_scan',
} as const;

/**
 * @nullable
 */
export type CreateChallengeRequestConfig = {
  targetAssetId?: string;
  question?: string;
  options?: string[];
  correctIndex?: number;
  cvTargetId?: string;
  cvTargetLabel?: string;
} | null;

export interface CreateChallengeRequest {
  type: CreateChallengeRequestType;
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  instructions?: string;
  /** @minimum 0 */
  points?: number;
  /** @nullable */
  config?: CreateChallengeRequestConfig;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /**
   * @maxLength 120
   * @nullable
   */
  room?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  presenter?: string | null;
  /**
   * @maxLength 500
   * @nullable
   */
  sessionQuestion?: string | null;
}

/**
 * @nullable
 */
export type UpdateChallengeRequestConfig = {
  targetAssetId?: string;
  question?: string;
  options?: string[];
  correctIndex?: number;
  cvTargetId?: string;
  cvTargetLabel?: string;
} | null;

export interface UpdateChallengeRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title?: string;
  instructions?: string;
  /** @minimum 0 */
  points?: number;
  /** @nullable */
  config?: UpdateChallengeRequestConfig;
  /**
   * Optional session start on the event clock (HH:mm 24-hour)
   * @nullable
   */
  startTime?: string | null;
  /**
   * Optional session end on the event clock (HH:mm 24-hour)
   * @nullable
   */
  endTime?: string | null;
  /**
   * @maxLength 120
   * @nullable
   */
  room?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  presenter?: string | null;
  /**
   * @maxLength 500
   * @nullable
   */
  sessionQuestion?: string | null;
}

/**
 * Missing or invalid bearer token
 */
export type UnauthenticatedResponse = Error;

/**
 * Authenticated but ownership policy cannot be evaluated or denied
 */
export type ForbiddenResponse = Error;

/**
 * Authentication is disabled or incomplete; protected routes fail closed
 */
export type AuthUnavailableResponse = Error;

/**
 * Invalid request payload or query parameters
 */
export type BadRequestResponse = Error;

/**
 * Authentication required or token invalid
 */
export type UnauthorizedResponse = Error;

/**
 * Resource not found
 */
export type NotFoundResponse = Error;

/**
 * Resource already exists or state conflict
 */
export type ConflictResponse = Error;

/**
 * Rate limit exceeded
 */
export type RateLimitedResponse = Error;

/**
 * Database or storage unavailable
 */
export type ServiceUnavailableResponse = Error;

export type GetDashboardTimeseriesParams = {
/**
 * @minimum 7
 * @maximum 90
 */
days?: number;
};

export type ListItemsParams = {
status?: ItemStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UploadFileBody = {
  file: Blob;
};

export type GetCivicRegionsParams = {
tenantId?: string;
};

export type GetCivicCategoriesParams = {
/**
 * Purpose slug to filter categories (e.g. councils, farms)
 */
purpose?: GetCivicCategoriesPurpose;
};

export type GetCivicCategoriesPurpose = typeof GetCivicCategoriesPurpose[keyof typeof GetCivicCategoriesPurpose];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetCivicCategoriesPurpose = {
  councils: 'councils',
  farms: 'farms',
  'workplace-safety': 'workplace-safety',
  facilities: 'facilities',
  'homes-rentals': 'homes-rentals',
  'treasure-hunt': 'treasure-hunt',
} as const;

export type GetCivicAssetsParams = {
regionId?: string;
tenantId?: string;
assetTypeId?: string;
minLng?: number;
minLat?: number;
maxLng?: number;
maxLat?: number;
/**
 * @minimum 1
 * @maximum 500
 */
limit?: number;
};

export type GetCivicReportSummaryParams = {
tenantId?: string;
};

export type UploadReportPhotoBody = {
  photo: Blob;
};

export type GetNearbyReportsParams = {
lat: number;
lng: number;
radius?: number;
categoryId: string;
};

export type GetMyReports200 = {
  reports: MyReport[];
};

export type DetectFromPhotoBody = {
  /** Storage blob key from upload-photo. */
  blobKey: string;
  /** Optional report latitude for asset matching. */
  latitude?: number;
  /** Optional report longitude for asset matching. */
  longitude?: number;
};

export type MatchNearestAssetParams = {
assetTypeSlug: string;
lat: number;
lng: number;
};

export type GetMapIssuesParams = {
/**
 * @minimum -180
 * @maximum 180
 */
minLng: number;
/**
 * @minimum -90
 * @maximum 90
 */
minLat: number;
/**
 * @minimum -180
 * @maximum 180
 */
maxLng: number;
/**
 * @minimum -90
 * @maximum 90
 */
maxLat: number;
categoryId?: string;
status?: GetMapIssuesStatus;
tenantId?: string;
dateFrom?: string;
dateTo?: string;
/**
 * @minimum 1
 * @maximum 2000
 */
limit?: number;
};

export type GetMapIssuesStatus = typeof GetMapIssuesStatus[keyof typeof GetMapIssuesStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetMapIssuesStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export type GetMapAssetsParams = {
/**
 * @minimum -180
 * @maximum 180
 */
minLng: number;
/**
 * @minimum -90
 * @maximum 90
 */
minLat: number;
/**
 * @minimum -180
 * @maximum 180
 */
maxLng: number;
/**
 * @minimum -90
 * @maximum 90
 */
maxLat: number;
tenantId?: string;
assetTypeId?: string;
regionId?: string;
/**
 * @minimum 1
 * @maximum 3000
 */
limit?: number;
};

export type ListTwinsParams = {
entityType?: ListTwinsEntityType;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTwinsEntityType = typeof ListTwinsEntityType[keyof typeof ListTwinsEntityType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ListTwinsEntityType = {
  event: 'event',
  location: 'location',
} as const;

export type ListEventTwinItemsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTwinPresenceParams = {
/**
 * @minLength 1
 * @maxLength 100
 */
sceneId: string;
/**
 * @minLength 1
 * @maxLength 128
 */
excludeSelf?: string;
};

export type LeaveTwinPresenceParams = {
/**
 * @minLength 1
 * @maxLength 100
 */
sceneId: string;
};

export type GetNearbyAssetsParams = {
/**
 * @minimum -90
 * @maximum 90
 */
lat: number;
/**
 * @minimum -180
 * @maximum 180
 */
lng: number;
/**
 * @minimum 1
 * @maximum 500
 */
radius?: number;
};

export type GetWorkspaceReportsParams = {
status?: GetWorkspaceReportsStatus;
categoryId?: string;
severity?: GetWorkspaceReportsSeverity;
assignedTo?: string;
unassigned?: GetWorkspaceReportsUnassigned;
sort?: GetWorkspaceReportsSort;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type GetWorkspaceReportsStatus = typeof GetWorkspaceReportsStatus[keyof typeof GetWorkspaceReportsStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceReportsStatus = {
  submitted: 'submitted',
  acknowledged: 'acknowledged',
  in_progress: 'in_progress',
  scheduled: 'scheduled',
  resolved: 'resolved',
  closed: 'closed',
  rejected: 'rejected',
  duplicate: 'duplicate',
} as const;

export type GetWorkspaceReportsSeverity = typeof GetWorkspaceReportsSeverity[keyof typeof GetWorkspaceReportsSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceReportsSeverity = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent',
} as const;

export type GetWorkspaceReportsUnassigned = typeof GetWorkspaceReportsUnassigned[keyof typeof GetWorkspaceReportsUnassigned];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceReportsUnassigned = {
  true: 'true',
  false: 'false',
} as const;

export type GetWorkspaceReportsSort = typeof GetWorkspaceReportsSort[keyof typeof GetWorkspaceReportsSort];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceReportsSort = {
  newest: 'newest',
  oldest: 'oldest',
  upvotes: 'upvotes',
  severity: 'severity',
} as const;

export type AssignWorkspaceReportBody = {
  assignedToStaffId: string;
};

export type MergeWorkspaceReportBody = {
  canonicalReportId: string;
};

export type GetWorkspaceAssetsParams = {
search?: string;
assetTypeId?: string;
condition?: GetWorkspaceAssetsCondition;
regionId?: string;
includeRetired?: GetWorkspaceAssetsIncludeRetired;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
};

export type GetWorkspaceAssetsCondition = typeof GetWorkspaceAssetsCondition[keyof typeof GetWorkspaceAssetsCondition];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceAssetsCondition = {
  good: 'good',
  fair: 'fair',
  poor: 'poor',
  damaged: 'damaged',
} as const;

export type GetWorkspaceAssetsIncludeRetired = typeof GetWorkspaceAssetsIncludeRetired[keyof typeof GetWorkspaceAssetsIncludeRetired];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GetWorkspaceAssetsIncludeRetired = {
  true: 'true',
  false: 'false',
} as const;

export type CreateWorkspaceAsset201 = {
  id?: string;
};

export type RetireWorkspaceAsset200 = {
  id?: string;
  retiredAt?: string;
};

export type BulkRetireWorkspaceAssetsBody = {
  /**
   * @minItems 1
   * @maxItems 100
   */
  assetIds: string[];
};

export type BulkRetireWorkspaceAssets200 = {
  retiredCount?: number;
};

export type CreateWorkspaceAssetType201 = {
  id?: string;
};

/**
 * GeoJSON Polygon or MultiPolygon
 */
export type UpdateWorkspaceRegionBoundaryBodyBoundaryGeojson = { [key: string]: unknown };

export type UpdateWorkspaceRegionBoundaryBody = {
  /** GeoJSON Polygon or MultiPolygon */
  boundaryGeojson: UpdateWorkspaceRegionBoundaryBodyBoundaryGeojson;
};

export type UploadImportFileBody = {
  file?: Blob;
};

export type CommitImportBody = {
  sessionId: string;
  regionId?: string;
};

export type GetImportHistoryParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ApproveCategorySuggestionBody = {
  /**
   * Optional asset-type mapping for the new category.
   * @nullable
   */
  assetTypeId?: string | null;
};

export type GetGeocodeSearchParams = {
/**
 * Address search text (minimum 3 characters).
 * @minLength 3
 * @maxLength 200
 */
q: string;
};

export type ListPublicHuntsParams = {
/**
 * Filter by hunt status. Defaults to active.
 */
status?: ListPublicHuntsStatus;
};

export type ListPublicHuntsStatus = typeof ListPublicHuntsStatus[keyof typeof ListPublicHuntsStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ListPublicHuntsStatus = {
  active: 'active',
  closed: 'closed',
  all: 'all',
} as const;

export type GetHuntLeaderboardParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



/**
 * @summary Liveness probe
 */
export const getGetHealthUrl = () => {


  

  return `/api/health`
}

export const getHealth = async ( options?: RequestInit): Promise<Health> => {
  
  return customFetch<Health>(getGetHealthUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetHealthQueryKey = () => {
    return [
    `/api/health`
    ] as const;
    }

    
export const getGetHealthQueryOptions = <TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHealthQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) => getHealth({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>
export type GetHealthQueryError = unknown


/**
 * @summary Liveness probe
 */

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHealthQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Owner-scoped counts for the authenticated principal only.

 * @summary Aggregate counters for the caller's owned items
 */
export const getGetDashboardStatsUrl = () => {


  

  return `/api/dashboard/stats`
}

export const getDashboardStats = async ( options?: RequestInit): Promise<DashboardStats> => {
  
  return customFetch<DashboardStats>(getGetDashboardStatsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetDashboardStatsQueryKey = () => {
    return [
    `/api/dashboard/stats`
    ] as const;
    }

    
export const getGetDashboardStatsQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardStatsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardStats>>> = ({ signal }) => getDashboardStats({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardStats>>>
export type GetDashboardStatsQueryError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse


/**
 * @summary Aggregate counters for the caller's owned items
 */

export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardStatsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Daily activity series for the dashboard chart
 */
export const getGetDashboardTimeseriesUrl = (params?: GetDashboardTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/dashboard/timeseries?${stringifiedParams}` : `/api/dashboard/timeseries`
}

export const getDashboardTimeseries = async (params?: GetDashboardTimeseriesParams, options?: RequestInit): Promise<Timeseries> => {
  
  return customFetch<Timeseries>(getGetDashboardTimeseriesUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetDashboardTimeseriesQueryKey = (params?: GetDashboardTimeseriesParams,) => {
    return [
    `/api/dashboard/timeseries`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetDashboardTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardTimeseriesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardTimeseries>>> = ({ signal }) => getDashboardTimeseries(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardTimeseries>>>
export type GetDashboardTimeseriesQueryError = unknown


/**
 * @summary Daily activity series for the dashboard chart
 */

export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(
 params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardTimeseriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Owner-scoped. Returns only items created by the authenticated Entra identity
(tenant id + object id). Requires a valid bearer access token.

 * @summary List items owned by the caller
 */
export const getListItemsUrl = (params?: ListItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/items?${stringifiedParams}` : `/api/items`
}

export const listItems = async (params?: ListItemsParams, options?: RequestInit): Promise<ItemList> => {
  
  return customFetch<ItemList>(getListItemsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListItemsQueryKey = (params?: ListItemsParams,) => {
    return [
    `/api/items`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListItemsQueryOptions = <TData = Awaited<ReturnType<typeof listItems>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>(params?: ListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListItemsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listItems>>> = ({ signal }) => listItems(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listItems>>>
export type ListItemsQueryError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse


/**
 * @summary List items owned by the caller
 */

export function useListItems<TData = Awaited<ReturnType<typeof listItems>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>(
 params?: ListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListItemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Owner-scoped. The authenticated caller becomes the sole owner of the new item.

 * @summary Create an item owned by the caller
 */
export const getCreateItemUrl = () => {


  

  return `/api/items`
}

export const createItem = async (itemCreate: ItemCreate, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getCreateItemUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      itemCreate,)
  }
);}




export const getCreateItemMutationOptions = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext> => {

const mutationKey = ['createItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createItem>>, {data: ItemCreate}> = (props) => {
          const {data} = props ?? {};

          return  createItem(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateItemMutationResult = NonNullable<Awaited<ReturnType<typeof createItem>>>
    export type CreateItemMutationBody = ItemCreate
    export type CreateItemMutationError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse

    /**
 * @summary Create an item owned by the caller
 */
export const useCreateItem = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createItem>>,
        TError,
        {data: ItemCreate},
        TContext
      > => {

      const mutationOptions = getCreateItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Owner-scoped. Non-owned ids are reported as not found.

 * @summary Get one owned item
 */
export const getGetItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const getItem = async (id: string, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getGetItemUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetItemQueryKey = (id?: string,) => {
    return [
    `/api/items/${id}`
    ] as const;
    }

    
export const getGetItemQueryOptions = <TData = Awaited<ReturnType<typeof getItem>>, TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetItemQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getItem>>> = ({ signal }) => getItem(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData> & { queryKey: QueryKey }
}

export type GetItemQueryResult = NonNullable<Awaited<ReturnType<typeof getItem>>>
export type GetItemQueryError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse


/**
 * @summary Get one owned item
 */

export function useGetItem<TData = Awaited<ReturnType<typeof getItem>>, TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetItemQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Owner-scoped. Updates succeed only for items owned by the authenticated caller.

 * @summary Update an owned item
 */
export const getUpdateItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const updateItem = async (id: string,
    itemUpdate: ItemUpdate, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getUpdateItemUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      itemUpdate,)
  }
);}




export const getUpdateItemMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext> => {

const mutationKey = ['updateItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateItem>>, {id: string;data: ItemUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateItem(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateItemMutationResult = NonNullable<Awaited<ReturnType<typeof updateItem>>>
    export type UpdateItemMutationBody = ItemUpdate
    export type UpdateItemMutationError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse

    /**
 * @summary Update an owned item
 */
export const useUpdateItem = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateItem>>,
        TError,
        {id: string;data: ItemUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Owner-scoped. Deletes succeed only for items owned by the authenticated caller.

 * @summary Delete an owned item
 */
export const getDeleteItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const deleteItem = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteItemUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteItemMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteItem>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteItem(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteItemMutationResult = NonNullable<Awaited<ReturnType<typeof deleteItem>>>
    
    export type DeleteItemMutationError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse

    /**
 * @summary Delete an owned item
 */
export const useDeleteItem = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteItem>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Public (unauthenticated) endpoint. Returns the normalized state of the
database, storage, AI, and auth plugins. Only non-secret values are
included (e.g. the MSAL public client config). Never returns keys,
connection strings, or SAS URLs.

 * @summary Availability and public config of platform plugins
 */
export const getGetPluginsStatusUrl = () => {


  

  return `/api/plugins/status`
}

export const getPluginsStatus = async ( options?: RequestInit): Promise<PluginsStatus> => {
  
  return customFetch<PluginsStatus>(getGetPluginsStatusUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetPluginsStatusQueryKey = () => {
    return [
    `/api/plugins/status`
    ] as const;
    }

    
export const getGetPluginsStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPluginsStatusQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPluginsStatus>>> = ({ signal }) => getPluginsStatus({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPluginsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPluginsStatus>>>
export type GetPluginsStatusQueryError = unknown


/**
 * @summary Availability and public config of platform plugins
 */

export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPluginsStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Current authenticated user
 */
export const getGetCurrentUserUrl = () => {


  

  return `/api/auth/me`
}

export const getCurrentUser = async ( options?: RequestInit): Promise<CurrentUser> => {
  
  return customFetch<CurrentUser>(getGetCurrentUserUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCurrentUserQueryKey = () => {
    return [
    `/api/auth/me`
    ] as const;
    }

    
export const getGetCurrentUserQueryOptions = <TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) => getCurrentUser({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>
export type GetCurrentUserQueryError = Error


/**
 * @summary Current authenticated user
 */

export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCurrentUserQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Server-side LLM proxy. The client may only send a message and an
optional conversation id — model, endpoint, and credentials are
resolved exclusively on the server. Requires a valid bearer access
token — anonymous AI access is never allowed (paid capability).

 * @summary Send a chat message to the app assistant
 */
export const getPostAiChatUrl = () => {


  

  return `/api/ai/chat`
}

export const postAiChat = async (chatRequest: ChatRequest, options?: RequestInit): Promise<ChatResponse> => {
  
  return customFetch<ChatResponse>(getPostAiChatUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      chatRequest,)
  }
);}




export const getPostAiChatMutationOptions = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext> => {

const mutationKey = ['postAiChat'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postAiChat>>, {data: ChatRequest}> = (props) => {
          const {data} = props ?? {};

          return  postAiChat(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type PostAiChatMutationResult = NonNullable<Awaited<ReturnType<typeof postAiChat>>>
    export type PostAiChatMutationBody = ChatRequest
    export type PostAiChatMutationError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse

    /**
 * @summary Send a chat message to the app assistant
 */
export const usePostAiChat = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof postAiChat>>,
        TError,
        {data: ChatRequest},
        TContext
      > => {

      const mutationOptions = getPostAiChatMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Owner-scoped. Returns only files owned by the authenticated caller.
Requires a valid bearer access token — anonymous access is never allowed.

 * @summary List stored files
 */
export const getListFilesUrl = () => {


  

  return `/api/files`
}

export const listFiles = async ( options?: RequestInit): Promise<FileList> => {
  
  return customFetch<FileList>(getListFilesUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListFilesQueryKey = () => {
    return [
    `/api/files`
    ] as const;
    }

    
export const getListFilesQueryOptions = <TData = Awaited<ReturnType<typeof listFiles>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFilesQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFiles>>> = ({ signal }) => listFiles({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listFiles>>>
export type ListFilesQueryError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse


/**
 * @summary List stored files
 */

export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFilesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Owner-scoped. The authenticated caller becomes the owner of the uploaded
file. Requires a valid bearer access token. Uploads are rate-limited
per identity to prevent storage cost amplification.

 * @summary Upload a file
 */
export const getUploadFileUrl = () => {


  

  return `/api/files`
}

export const uploadFile = async (uploadFileBody: UploadFileBody, options?: RequestInit): Promise<StoredFile> => {
    const formData = new FormData();
formData.append(`file`, uploadFileBody.file)

  return customFetch<StoredFile>(getUploadFileUrl(),
  {      
    ...options,
    method: 'POST'
    ,
    body: 
      formData,
  }
);}




export const getUploadFileMutationOptions = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext> => {

const mutationKey = ['uploadFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadFile>>, {data: UploadFileBody}> = (props) => {
          const {data} = props ?? {};

          return  uploadFile(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadFile>>>
    export type UploadFileMutationBody = UploadFileBody
    export type UploadFileMutationError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse

    /**
 * @summary Upload a file
 */
export const useUploadFile = <TError = Error | UnauthenticatedResponse | ForbiddenResponse | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadFile>>,
        TError,
        {data: UploadFileBody},
        TContext
      > => {

      const mutationOptions = getUploadFileMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Owner-scoped. Deletes succeed only for files owned by the authenticated
caller. Non-owned ids are reported as not found (no existence leak).

 * @summary Delete a stored file
 */
export const getDeleteFileUrl = (id: string,) => {


  

  return `/api/files/${id}`
}

export const deleteFile = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteFileUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteFileMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFile>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteFile(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFile>>>
    
    export type DeleteFileMutationError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse

    /**
 * @summary Delete a stored file
 */
export const useDeleteFile = <TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteFile>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteFileMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Owner-scoped. Downloads succeed only for files owned by the authenticated
caller. Non-owned ids are reported as not found (no existence leak).

 * @summary Download file content
 */
export const getDownloadFileUrl = (id: string,) => {


  

  return `/api/files/${id}/content`
}

export const downloadFile = async (id: string, options?: RequestInit): Promise<Blob> => {
  
  return customFetch<Blob>(getDownloadFileUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getDownloadFileQueryKey = (id?: string,) => {
    return [
    `/api/files/${id}/content`
    ] as const;
    }

    
export const getDownloadFileQueryOptions = <TData = Awaited<ReturnType<typeof downloadFile>>, TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadFileQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({ signal }) => downloadFile(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadFileQueryResult = NonNullable<Awaited<ReturnType<typeof downloadFile>>>
export type DownloadFileQueryError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse


/**
 * @summary Download file content
 */

export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = UnauthenticatedResponse | ForbiddenResponse | Error | AuthUnavailableResponse>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadFileQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List all tenants (councils and service providers)
 */
export const getGetCivicTenantsUrl = () => {


  

  return `/api/civic/tenants`
}

export const getCivicTenants = async ( options?: RequestInit): Promise<TenantList> => {
  
  return customFetch<TenantList>(getGetCivicTenantsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicTenantsQueryKey = () => {
    return [
    `/api/civic/tenants`
    ] as const;
    }

    
export const getGetCivicTenantsQueryOptions = <TData = Awaited<ReturnType<typeof getCivicTenants>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicTenants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicTenantsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicTenants>>> = ({ signal }) => getCivicTenants({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicTenants>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicTenantsQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicTenants>>>
export type GetCivicTenantsQueryError = Error


/**
 * @summary List all tenants (councils and service providers)
 */

export function useGetCivicTenants<TData = Awaited<ReturnType<typeof getCivicTenants>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicTenants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicTenantsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List regions with boundary GeoJSON as a FeatureCollection
 */
export const getGetCivicRegionsUrl = (params?: GetCivicRegionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/civic/regions?${stringifiedParams}` : `/api/civic/regions`
}

export const getCivicRegions = async (params?: GetCivicRegionsParams, options?: RequestInit): Promise<RegionFeatureCollection> => {
  
  return customFetch<RegionFeatureCollection>(getGetCivicRegionsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicRegionsQueryKey = (params?: GetCivicRegionsParams,) => {
    return [
    `/api/civic/regions`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetCivicRegionsQueryOptions = <TData = Awaited<ReturnType<typeof getCivicRegions>>, TError = Error>(params?: GetCivicRegionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicRegions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicRegionsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicRegions>>> = ({ signal }) => getCivicRegions(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicRegions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicRegionsQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicRegions>>>
export type GetCivicRegionsQueryError = Error


/**
 * @summary List regions with boundary GeoJSON as a FeatureCollection
 */

export function useGetCivicRegions<TData = Awaited<ReturnType<typeof getCivicRegions>>, TError = Error>(
 params?: GetCivicRegionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicRegions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicRegionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List the asset type taxonomy
 */
export const getGetCivicAssetTypesUrl = () => {


  

  return `/api/civic/asset-types`
}

export const getCivicAssetTypes = async ( options?: RequestInit): Promise<AssetTypeList> => {
  
  return customFetch<AssetTypeList>(getGetCivicAssetTypesUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicAssetTypesQueryKey = () => {
    return [
    `/api/civic/asset-types`
    ] as const;
    }

    
export const getGetCivicAssetTypesQueryOptions = <TData = Awaited<ReturnType<typeof getCivicAssetTypes>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicAssetTypes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicAssetTypesQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicAssetTypes>>> = ({ signal }) => getCivicAssetTypes({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicAssetTypes>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicAssetTypesQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicAssetTypes>>>
export type GetCivicAssetTypesQueryError = Error


/**
 * @summary List the asset type taxonomy
 */

export function useGetCivicAssetTypes<TData = Awaited<ReturnType<typeof getCivicAssetTypes>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicAssetTypes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicAssetTypesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Returns issue categories for the report wizard and filters. Pass `purpose` to scope the list to a persona (councils, farms, workplace-safety, facilities, homes-rentals, treasure-hunt). Categories with an empty contexts array are always included. Omit `purpose` to return the full catalogue (map/workspace filters).

 * @summary List issue categories with optional asset-type mapping
 */
export const getGetCivicCategoriesUrl = (params?: GetCivicCategoriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/civic/categories?${stringifiedParams}` : `/api/civic/categories`
}

export const getCivicCategories = async (params?: GetCivicCategoriesParams, options?: RequestInit): Promise<CategoryList> => {
  
  return customFetch<CategoryList>(getGetCivicCategoriesUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicCategoriesQueryKey = (params?: GetCivicCategoriesParams,) => {
    return [
    `/api/civic/categories`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetCivicCategoriesQueryOptions = <TData = Awaited<ReturnType<typeof getCivicCategories>>, TError = Error>(params?: GetCivicCategoriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicCategories>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicCategoriesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicCategories>>> = ({ signal }) => getCivicCategories(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicCategories>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicCategoriesQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicCategories>>>
export type GetCivicCategoriesQueryError = Error


/**
 * @summary List issue categories with optional asset-type mapping
 */

export function useGetCivicCategories<TData = Awaited<ReturnType<typeof getCivicCategories>>, TError = Error>(
 params?: GetCivicCategoriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicCategories>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicCategoriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List assets, filterable by region or bounding box
 */
export const getGetCivicAssetsUrl = (params?: GetCivicAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/civic/assets?${stringifiedParams}` : `/api/civic/assets`
}

export const getCivicAssets = async (params?: GetCivicAssetsParams, options?: RequestInit): Promise<AssetList> => {
  
  return customFetch<AssetList>(getGetCivicAssetsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicAssetsQueryKey = (params?: GetCivicAssetsParams,) => {
    return [
    `/api/civic/assets`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetCivicAssetsQueryOptions = <TData = Awaited<ReturnType<typeof getCivicAssets>>, TError = Error>(params?: GetCivicAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicAssetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicAssets>>> = ({ signal }) => getCivicAssets(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicAssets>>>
export type GetCivicAssetsQueryError = Error


/**
 * @summary List assets, filterable by region or bounding box
 */

export function useGetCivicAssets<TData = Awaited<ReturnType<typeof getCivicAssets>>, TError = Error>(
 params?: GetCivicAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicAssetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Public report status counts (optionally scoped to a tenant)
 */
export const getGetCivicReportSummaryUrl = (params?: GetCivicReportSummaryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/civic/reports/summary?${stringifiedParams}` : `/api/civic/reports/summary`
}

export const getCivicReportSummary = async (params?: GetCivicReportSummaryParams, options?: RequestInit): Promise<ReportSummary> => {
  
  return customFetch<ReportSummary>(getGetCivicReportSummaryUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCivicReportSummaryQueryKey = (params?: GetCivicReportSummaryParams,) => {
    return [
    `/api/civic/reports/summary`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetCivicReportSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getCivicReportSummary>>, TError = Error>(params?: GetCivicReportSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicReportSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCivicReportSummaryQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCivicReportSummary>>> = ({ signal }) => getCivicReportSummary(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCivicReportSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCivicReportSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getCivicReportSummary>>>
export type GetCivicReportSummaryQueryError = Error


/**
 * @summary Public report status counts (optionally scoped to a tenant)
 */

export function useGetCivicReportSummary<TData = Awaited<ReturnType<typeof getCivicReportSummary>>, TError = Error>(
 params?: GetCivicReportSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCivicReportSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCivicReportSummaryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Upload a photo for a report (anonymous, rate-limited)
 */
export const getUploadReportPhotoUrl = () => {


  

  return `/api/reports/upload-photo`
}

export const uploadReportPhoto = async (uploadReportPhotoBody: UploadReportPhotoBody, options?: RequestInit): Promise<PhotoUploadResult> => {
    const formData = new FormData();
formData.append(`photo`, uploadReportPhotoBody.photo)

  return customFetch<PhotoUploadResult>(getUploadReportPhotoUrl(),
  {      
    ...options,
    method: 'POST'
    ,
    body: 
      formData,
  }
);}




export const getUploadReportPhotoMutationOptions = <TError = BadRequestResponse | RateLimitedResponse | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadReportPhoto>>, TError,{data: UploadReportPhotoBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadReportPhoto>>, TError,{data: UploadReportPhotoBody}, TContext> => {

const mutationKey = ['uploadReportPhoto'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadReportPhoto>>, {data: UploadReportPhotoBody}> = (props) => {
          const {data} = props ?? {};

          return  uploadReportPhoto(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UploadReportPhotoMutationResult = NonNullable<Awaited<ReturnType<typeof uploadReportPhoto>>>
    export type UploadReportPhotoMutationBody = UploadReportPhotoBody
    export type UploadReportPhotoMutationError = BadRequestResponse | RateLimitedResponse | Error

    /**
 * @summary Upload a photo for a report (anonymous, rate-limited)
 */
export const useUploadReportPhoto = <TError = BadRequestResponse | RateLimitedResponse | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadReportPhoto>>, TError,{data: UploadReportPhotoBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadReportPhoto>>,
        TError,
        {data: UploadReportPhotoBody},
        TContext
      > => {

      const mutationOptions = getUploadReportPhotoMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Submit a new anonymous report (rate-limited)
 */
export const getCreateReportUrl = () => {


  

  return `/api/reports`
}

export const createReport = async (createReportRequest: CreateReportRequest, options?: RequestInit): Promise<CreateReportResult> => {
  
  return customFetch<CreateReportResult>(getCreateReportUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createReportRequest,)
  }
);}




export const getCreateReportMutationOptions = <TError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createReport>>, TError,{data: CreateReportRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createReport>>, TError,{data: CreateReportRequest}, TContext> => {

const mutationKey = ['createReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createReport>>, {data: CreateReportRequest}> = (props) => {
          const {data} = props ?? {};

          return  createReport(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateReportMutationResult = NonNullable<Awaited<ReturnType<typeof createReport>>>
    export type CreateReportMutationBody = CreateReportRequest
    export type CreateReportMutationError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Submit a new anonymous report (rate-limited)
 */
export const useCreateReport = <TError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createReport>>, TError,{data: CreateReportRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createReport>>,
        TError,
        {data: CreateReportRequest},
        TContext
      > => {

      const mutationOptions = getCreateReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Find nearby open reports for duplicate deflection
 */
export const getGetNearbyReportsUrl = (params: GetNearbyReportsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/nearby?${stringifiedParams}` : `/api/reports/nearby`
}

export const getNearbyReports = async (params: GetNearbyReportsParams, options?: RequestInit): Promise<NearbyReportList> => {
  
  return customFetch<NearbyReportList>(getGetNearbyReportsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetNearbyReportsQueryKey = (params?: GetNearbyReportsParams,) => {
    return [
    `/api/reports/nearby`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetNearbyReportsQueryOptions = <TData = Awaited<ReturnType<typeof getNearbyReports>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params: GetNearbyReportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNearbyReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNearbyReportsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNearbyReports>>> = ({ signal }) => getNearbyReports(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNearbyReports>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNearbyReportsQueryResult = NonNullable<Awaited<ReturnType<typeof getNearbyReports>>>
export type GetNearbyReportsQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary Find nearby open reports for duplicate deflection
 */

export function useGetNearbyReports<TData = Awaited<ReturnType<typeof getNearbyReports>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params: GetNearbyReportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNearbyReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNearbyReportsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Attaches an unowned report to the caller's account using the tracking
token. Idempotent when the report is already owned by the same user.
Returns 409 if the report is owned by a different user.

 * @summary Claim an anonymous report for the signed-in user (attach to account)
 */
export const getClaimReportUrl = (token: string,) => {


  

  return `/api/reports/${token}/claim`
}

export const claimReport = async (token: string, options?: RequestInit): Promise<ClaimReportResult> => {
  
  return customFetch<ClaimReportResult>(getClaimReportUrl(token),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getClaimReportMutationOptions = <TError = UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof claimReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof claimReport>>, TError,{token: string}, TContext> => {

const mutationKey = ['claimReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof claimReport>>, {token: string}> = (props) => {
          const {token} = props ?? {};

          return  claimReport(token,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type ClaimReportMutationResult = NonNullable<Awaited<ReturnType<typeof claimReport>>>
    
    export type ClaimReportMutationError = UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse

    /**
 * @summary Claim an anonymous report for the signed-in user (attach to account)
 */
export const useClaimReport = <TError = UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof claimReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof claimReport>>,
        TError,
        {token: string},
        TContext
      > => {

      const mutationOptions = getClaimReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Returns reports whose `reporter_user_id` matches the caller's Entra
object id — i.e. reports submitted while signed in or claimed via a
tracking link. Never matches by email. Ordered newest first.

 * @summary List reports owned by the signed-in user
 */
export const getGetMyReportsUrl = () => {


  

  return `/api/my/reports`
}

export const getMyReports = async ( options?: RequestInit): Promise<GetMyReports200> => {
  
  return customFetch<GetMyReports200>(getGetMyReportsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetMyReportsQueryKey = () => {
    return [
    `/api/my/reports`
    ] as const;
    }

    
export const getGetMyReportsQueryOptions = <TData = Awaited<ReturnType<typeof getMyReports>>, TError = UnauthorizedResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyReportsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyReports>>> = ({ signal }) => getMyReports({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyReports>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMyReportsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyReports>>>
export type GetMyReportsQueryError = UnauthorizedResponse


/**
 * @summary List reports owned by the signed-in user
 */

export function useGetMyReports<TData = Awaited<ReturnType<typeof getMyReports>>, TError = UnauthorizedResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMyReportsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Add support to an existing report (upvote for duplicate deflection)
 */
export const getUpvoteReportUrl = (token: string,) => {


  

  return `/api/reports/${token}/upvote`
}

export const upvoteReport = async (token: string, options?: RequestInit): Promise<UpvoteResult> => {
  
  return customFetch<UpvoteResult>(getUpvoteReportUrl(token),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getUpvoteReportMutationOptions = <TError = NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upvoteReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof upvoteReport>>, TError,{token: string}, TContext> => {

const mutationKey = ['upvoteReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upvoteReport>>, {token: string}> = (props) => {
          const {token} = props ?? {};

          return  upvoteReport(token,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpvoteReportMutationResult = NonNullable<Awaited<ReturnType<typeof upvoteReport>>>
    
    export type UpvoteReportMutationError = NotFoundResponse | RateLimitedResponse

    /**
 * @summary Add support to an existing report (upvote for duplicate deflection)
 */
export const useUpvoteReport = <TError = NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upvoteReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof upvoteReport>>,
        TError,
        {token: string},
        TContext
      > => {

      const mutationOptions = getUpvoteReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Send a nudge for an idle report (rate-limited, 1 per 24h)
 */
export const getNudgeReportUrl = (token: string,) => {


  

  return `/api/reports/${token}/nudge`
}

export const nudgeReport = async (token: string, options?: RequestInit): Promise<NudgeResult> => {
  
  return customFetch<NudgeResult>(getNudgeReportUrl(token),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getNudgeReportMutationOptions = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof nudgeReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof nudgeReport>>, TError,{token: string}, TContext> => {

const mutationKey = ['nudgeReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof nudgeReport>>, {token: string}> = (props) => {
          const {token} = props ?? {};

          return  nudgeReport(token,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type NudgeReportMutationResult = NonNullable<Awaited<ReturnType<typeof nudgeReport>>>
    
    export type NudgeReportMutationError = BadRequestResponse | NotFoundResponse | RateLimitedResponse

    /**
 * @summary Send a nudge for an idle report (rate-limited, 1 per 24h)
 */
export const useNudgeReport = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof nudgeReport>>, TError,{token: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof nudgeReport>>,
        TError,
        {token: string},
        TContext
      > => {

      const mutationOptions = getNudgeReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Only the signed-in owner of the report (reporter_user_id matches the
caller's Entra oid) may post. The comment is stored as a public,
non-internal update attributed to the reporter.

 * @summary Add a public comment to a report you own (requires sign-in)
 */
export const getAddReportCommentUrl = (token: string,) => {


  

  return `/api/reports/${token}/comments`
}

export const addReportComment = async (token: string,
    addReportCommentRequest: AddReportCommentRequest, options?: RequestInit): Promise<ReportCommentResult> => {
  
  return customFetch<ReportCommentResult>(getAddReportCommentUrl(token),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      addReportCommentRequest,)
  }
);}




export const getAddReportCommentMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | Error | NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addReportComment>>, TError,{token: string;data: AddReportCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addReportComment>>, TError,{token: string;data: AddReportCommentRequest}, TContext> => {

const mutationKey = ['addReportComment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addReportComment>>, {token: string;data: AddReportCommentRequest}> = (props) => {
          const {token,data} = props ?? {};

          return  addReportComment(token,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type AddReportCommentMutationResult = NonNullable<Awaited<ReturnType<typeof addReportComment>>>
    export type AddReportCommentMutationBody = AddReportCommentRequest
    export type AddReportCommentMutationError = BadRequestResponse | UnauthorizedResponse | Error | NotFoundResponse | RateLimitedResponse

    /**
 * @summary Add a public comment to a report you own (requires sign-in)
 */
export const useAddReportComment = <TError = BadRequestResponse | UnauthorizedResponse | Error | NotFoundResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addReportComment>>, TError,{token: string;data: AddReportCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addReportComment>>,
        TError,
        {token: string;data: AddReportCommentRequest},
        TContext
      > => {

      const mutationOptions = getAddReportCommentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * When the request includes a valid bearer token, the response includes
`isOwner` so the frontend can show the comment box for the report owner.

 * @summary Get report tracking details (public, privacy-filtered)
 */
export const getTrackReportUrl = (token: string,) => {


  

  return `/api/reports/track/${token}`
}

export const trackReport = async (token: string, options?: RequestInit): Promise<ReportTracking> => {
  
  return customFetch<ReportTracking>(getTrackReportUrl(token),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getTrackReportQueryKey = (token?: string,) => {
    return [
    `/api/reports/track/${token}`
    ] as const;
    }

    
export const getTrackReportQueryOptions = <TData = Awaited<ReturnType<typeof trackReport>>, TError = NotFoundResponse | ServiceUnavailableResponse>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getTrackReportQueryKey(token);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof trackReport>>> = ({ signal }) => trackReport(token, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(token), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof trackReport>>, TError, TData> & { queryKey: QueryKey }
}

export type TrackReportQueryResult = NonNullable<Awaited<ReturnType<typeof trackReport>>>
export type TrackReportQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get report tracking details (public, privacy-filtered)
 */

export function useTrackReport<TData = Awaited<ReturnType<typeof trackReport>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getTrackReportQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Serve the first photo of a report
 */
export const getGetReportPhotoUrl = (reportId: string,) => {


  

  return `/api/reports/${reportId}/photo`
}

export const getReportPhoto = async (reportId: string, options?: RequestInit): Promise<Blob> => {
  
  return customFetch<Blob>(getGetReportPhotoUrl(reportId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetReportPhotoQueryKey = (reportId?: string,) => {
    return [
    `/api/reports/${reportId}/photo`
    ] as const;
    }

    
export const getGetReportPhotoQueryOptions = <TData = Awaited<ReturnType<typeof getReportPhoto>>, TError = NotFoundResponse | ServiceUnavailableResponse>(reportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getReportPhoto>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetReportPhotoQueryKey(reportId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getReportPhoto>>> = ({ signal }) => getReportPhoto(reportId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(reportId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getReportPhoto>>, TError, TData> & { queryKey: QueryKey }
}

export type GetReportPhotoQueryResult = NonNullable<Awaited<ReturnType<typeof getReportPhoto>>>
export type GetReportPhotoQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Serve the first photo of a report
 */

export function useGetReportPhoto<TData = Awaited<ReturnType<typeof getReportPhoto>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 reportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getReportPhoto>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetReportPhotoQueryOptions(reportId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary AI vision detection of asset type, problem, and severity from a photo (anonymous, rate-limited)
 */
export const getDetectFromPhotoUrl = () => {


  

  return `/api/detect`
}

export const detectFromPhoto = async (detectFromPhotoBody: DetectFromPhotoBody, options?: RequestInit): Promise<DetectResponse> => {
  
  return customFetch<DetectResponse>(getDetectFromPhotoUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      detectFromPhotoBody,)
  }
);}




export const getDetectFromPhotoMutationOptions = <TError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof detectFromPhoto>>, TError,{data: DetectFromPhotoBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof detectFromPhoto>>, TError,{data: DetectFromPhotoBody}, TContext> => {

const mutationKey = ['detectFromPhoto'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof detectFromPhoto>>, {data: DetectFromPhotoBody}> = (props) => {
          const {data} = props ?? {};

          return  detectFromPhoto(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DetectFromPhotoMutationResult = NonNullable<Awaited<ReturnType<typeof detectFromPhoto>>>
    export type DetectFromPhotoMutationBody = DetectFromPhotoBody
    export type DetectFromPhotoMutationError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary AI vision detection of asset type, problem, and severity from a photo (anonymous, rate-limited)
 */
export const useDetectFromPhoto = <TError = BadRequestResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof detectFromPhoto>>, TError,{data: DetectFromPhotoBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof detectFromPhoto>>,
        TError,
        {data: DetectFromPhotoBody},
        TContext
      > => {

      const mutationOptions = getDetectFromPhotoMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Find the nearest seeded asset of a type near a location (DB-only, no LLM)
 */
export const getMatchNearestAssetUrl = (params: MatchNearestAssetParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/detect/match-asset?${stringifiedParams}` : `/api/detect/match-asset`
}

export const matchNearestAsset = async (params: MatchNearestAssetParams, options?: RequestInit): Promise<MatchAssetResponse> => {
  
  return customFetch<MatchAssetResponse>(getMatchNearestAssetUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getMatchNearestAssetQueryKey = (params?: MatchNearestAssetParams,) => {
    return [
    `/api/detect/match-asset`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getMatchNearestAssetQueryOptions = <TData = Awaited<ReturnType<typeof matchNearestAsset>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params: MatchNearestAssetParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof matchNearestAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getMatchNearestAssetQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof matchNearestAsset>>> = ({ signal }) => matchNearestAsset(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof matchNearestAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type MatchNearestAssetQueryResult = NonNullable<Awaited<ReturnType<typeof matchNearestAsset>>>
export type MatchNearestAssetQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary Find the nearest seeded asset of a type near a location (DB-only, no LLM)
 */

export function useMatchNearestAsset<TData = Awaited<ReturnType<typeof matchNearestAsset>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params: MatchNearestAssetParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof matchNearestAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getMatchNearestAssetQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get issue reports as GeoJSON (bbox-filtered, never exposes private contact details)
 */
export const getGetMapIssuesUrl = (params: GetMapIssuesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/map/issues?${stringifiedParams}` : `/api/map/issues`
}

export const getMapIssues = async (params: GetMapIssuesParams, options?: RequestInit): Promise<IssueFeatureCollection> => {
  
  return customFetch<IssueFeatureCollection>(getGetMapIssuesUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetMapIssuesQueryKey = (params?: GetMapIssuesParams,) => {
    return [
    `/api/map/issues`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetMapIssuesQueryOptions = <TData = Awaited<ReturnType<typeof getMapIssues>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params: GetMapIssuesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMapIssues>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMapIssuesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMapIssues>>> = ({ signal }) => getMapIssues(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMapIssues>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMapIssuesQueryResult = NonNullable<Awaited<ReturnType<typeof getMapIssues>>>
export type GetMapIssuesQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary Get issue reports as GeoJSON (bbox-filtered, never exposes private contact details)
 */

export function useGetMapIssues<TData = Awaited<ReturnType<typeof getMapIssues>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params: GetMapIssuesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMapIssues>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMapIssuesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get infrastructure assets as GeoJSON (bbox-filtered with open issue counts)
 */
export const getGetMapAssetsUrl = (params: GetMapAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/map/assets?${stringifiedParams}` : `/api/map/assets`
}

export const getMapAssets = async (params: GetMapAssetsParams, options?: RequestInit): Promise<AssetFeatureCollection> => {
  
  return customFetch<AssetFeatureCollection>(getGetMapAssetsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetMapAssetsQueryKey = (params?: GetMapAssetsParams,) => {
    return [
    `/api/map/assets`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetMapAssetsQueryOptions = <TData = Awaited<ReturnType<typeof getMapAssets>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params: GetMapAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMapAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMapAssetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMapAssets>>> = ({ signal }) => getMapAssets(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMapAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMapAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof getMapAssets>>>
export type GetMapAssetsQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary Get infrastructure assets as GeoJSON (bbox-filtered with open issue counts)
 */

export function useGetMapAssets<TData = Awaited<ReturnType<typeof getMapAssets>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params: GetMapAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMapAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMapAssetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get a region summary with asset and issue counts
 */
export const getGetRegionSummaryUrl = (regionId: string,) => {


  

  return `/api/map/regions/${regionId}/summary`
}

export const getRegionSummary = async (regionId: string, options?: RequestInit): Promise<RegionSummary> => {
  
  return customFetch<RegionSummary>(getGetRegionSummaryUrl(regionId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetRegionSummaryQueryKey = (regionId?: string,) => {
    return [
    `/api/map/regions/${regionId}/summary`
    ] as const;
    }

    
export const getGetRegionSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getRegionSummary>>, TError = NotFoundResponse | ServiceUnavailableResponse>(regionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRegionSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRegionSummaryQueryKey(regionId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRegionSummary>>> = ({ signal }) => getRegionSummary(regionId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(regionId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRegionSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRegionSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getRegionSummary>>>
export type GetRegionSummaryQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get a region summary with asset and issue counts
 */

export function useGetRegionSummary<TData = Awaited<ReturnType<typeof getRegionSummary>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 regionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRegionSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRegionSummaryQueryOptions(regionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List registered event and location digital twins
 */
export const getListTwinsUrl = (params?: ListTwinsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/twins?${stringifiedParams}` : `/api/twins`
}

export const listTwins = async (params?: ListTwinsParams, options?: RequestInit): Promise<TwinCatalogResult> => {
  
  return customFetch<TwinCatalogResult>(getListTwinsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListTwinsQueryKey = (params?: ListTwinsParams,) => {
    return [
    `/api/twins`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListTwinsQueryOptions = <TData = Awaited<ReturnType<typeof listTwins>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params?: ListTwinsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTwins>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTwinsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTwins>>> = ({ signal }) => listTwins(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTwins>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTwinsQueryResult = NonNullable<Awaited<ReturnType<typeof listTwins>>>
export type ListTwinsQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary List registered event and location digital twins
 */

export function useListTwins<TData = Awaited<ReturnType<typeof listTwins>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params?: ListTwinsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTwins>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTwinsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get a digital twin scene definition for an event
 */
export const getGetEventTwinUrl = (eventSlug: string,) => {


  

  return `/api/events/${eventSlug}/twin`
}

export const getEventTwin = async (eventSlug: string, options?: RequestInit): Promise<EventTwinScene> => {
  
  return customFetch<EventTwinScene>(getGetEventTwinUrl(eventSlug),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetEventTwinQueryKey = (eventSlug?: string,) => {
    return [
    `/api/events/${eventSlug}/twin`
    ] as const;
    }

    
export const getGetEventTwinQueryOptions = <TData = Awaited<ReturnType<typeof getEventTwin>>, TError = NotFoundResponse | ServiceUnavailableResponse>(eventSlug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventTwin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventTwinQueryKey(eventSlug);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventTwin>>> = ({ signal }) => getEventTwin(eventSlug, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(eventSlug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEventTwin>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEventTwinQueryResult = NonNullable<Awaited<ReturnType<typeof getEventTwin>>>
export type GetEventTwinQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get a digital twin scene definition for an event
 */

export function useGetEventTwin<TData = Awaited<ReturnType<typeof getEventTwin>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 eventSlug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventTwin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEventTwinQueryOptions(eventSlug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List report and hunt-capture items placed in an event digital twin
 */
export const getListEventTwinItemsUrl = (eventSlug: string,
    params?: ListEventTwinItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/events/${eventSlug}/twin/items?${stringifiedParams}` : `/api/events/${eventSlug}/twin/items`
}

export const listEventTwinItems = async (eventSlug: string,
    params?: ListEventTwinItemsParams, options?: RequestInit): Promise<EventTwinItemList> => {
  
  return customFetch<EventTwinItemList>(getListEventTwinItemsUrl(eventSlug,params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListEventTwinItemsQueryKey = (eventSlug?: string,
    params?: ListEventTwinItemsParams,) => {
    return [
    `/api/events/${eventSlug}/twin/items`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListEventTwinItemsQueryOptions = <TData = Awaited<ReturnType<typeof listEventTwinItems>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(eventSlug: string,
    params?: ListEventTwinItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEventTwinItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEventTwinItemsQueryKey(eventSlug,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEventTwinItems>>> = ({ signal }) => listEventTwinItems(eventSlug,params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(eventSlug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEventTwinItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEventTwinItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listEventTwinItems>>>
export type ListEventTwinItemsQueryError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary List report and hunt-capture items placed in an event digital twin
 */

export function useListEventTwinItems<TData = Awaited<ReturnType<typeof listEventTwinItems>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(
 eventSlug: string,
    params?: ListEventTwinItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEventTwinItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEventTwinItemsQueryOptions(eventSlug,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Stream a photo texture for a wall-anchored twin item
 */
export const getGetEventTwinItemPhotoUrl = (eventSlug: string,
    itemId: string,) => {


  

  return `/api/events/${eventSlug}/twin/items/${itemId}/photo`
}

export const getEventTwinItemPhoto = async (eventSlug: string,
    itemId: string, options?: RequestInit): Promise<Blob> => {
  
  return customFetch<Blob>(getGetEventTwinItemPhotoUrl(eventSlug,itemId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetEventTwinItemPhotoQueryKey = (eventSlug?: string,
    itemId?: string,) => {
    return [
    `/api/events/${eventSlug}/twin/items/${itemId}/photo`
    ] as const;
    }

    
export const getGetEventTwinItemPhotoQueryOptions = <TData = Awaited<ReturnType<typeof getEventTwinItemPhoto>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(eventSlug: string,
    itemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventTwinItemPhoto>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventTwinItemPhotoQueryKey(eventSlug,itemId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventTwinItemPhoto>>> = ({ signal }) => getEventTwinItemPhoto(eventSlug,itemId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(eventSlug && itemId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEventTwinItemPhoto>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEventTwinItemPhotoQueryResult = NonNullable<Awaited<ReturnType<typeof getEventTwinItemPhoto>>>
export type GetEventTwinItemPhotoQueryError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Stream a photo texture for a wall-anchored twin item
 */

export function useGetEventTwinItemPhoto<TData = Awaited<ReturnType<typeof getEventTwinItemPhoto>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(
 eventSlug: string,
    itemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventTwinItemPhoto>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEventTwinItemPhotoQueryOptions(eventSlug,itemId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Show support for a report or verified hunt capture on an event twin
 */
export const getSupportEventTwinItemUrl = (eventSlug: string,
    itemId: string,) => {


  

  return `/api/events/${eventSlug}/twin/items/${itemId}/support`
}

export const supportEventTwinItem = async (eventSlug: string,
    itemId: string, options?: RequestInit): Promise<EventTwinItemSupportResult> => {
  
  return customFetch<EventTwinItemSupportResult>(getSupportEventTwinItemUrl(eventSlug,itemId),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getSupportEventTwinItemMutationOptions = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof supportEventTwinItem>>, TError,{eventSlug: string;itemId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof supportEventTwinItem>>, TError,{eventSlug: string;itemId: string}, TContext> => {

const mutationKey = ['supportEventTwinItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof supportEventTwinItem>>, {eventSlug: string;itemId: string}> = (props) => {
          const {eventSlug,itemId} = props ?? {};

          return  supportEventTwinItem(eventSlug,itemId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type SupportEventTwinItemMutationResult = NonNullable<Awaited<ReturnType<typeof supportEventTwinItem>>>
    
    export type SupportEventTwinItemMutationError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Show support for a report or verified hunt capture on an event twin
 */
export const useSupportEventTwinItem = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof supportEventTwinItem>>, TError,{eventSlug: string;itemId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof supportEventTwinItem>>,
        TError,
        {eventSlug: string;itemId: string},
        TContext
      > => {

      const mutationOptions = getSupportEventTwinItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Submit a photo-backed report placed on an event twin wall
 */
export const getCreateEventReportUrl = (eventSlug: string,) => {


  

  return `/api/events/${eventSlug}/reports`
}

export const createEventReport = async (eventSlug: string,
    createEventReportRequest: CreateEventReportRequest, options?: RequestInit): Promise<CreateEventReportResponse> => {
  
  return customFetch<CreateEventReportResponse>(getCreateEventReportUrl(eventSlug),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createEventReportRequest,)
  }
);}




export const getCreateEventReportMutationOptions = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventReport>>, TError,{eventSlug: string;data: CreateEventReportRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEventReport>>, TError,{eventSlug: string;data: CreateEventReportRequest}, TContext> => {

const mutationKey = ['createEventReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEventReport>>, {eventSlug: string;data: CreateEventReportRequest}> = (props) => {
          const {eventSlug,data} = props ?? {};

          return  createEventReport(eventSlug,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateEventReportMutationResult = NonNullable<Awaited<ReturnType<typeof createEventReport>>>
    export type CreateEventReportMutationBody = CreateEventReportRequest
    export type CreateEventReportMutationError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Submit a photo-backed report placed on an event twin wall
 */
export const useCreateEventReport = <TError = BadRequestResponse | NotFoundResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventReport>>, TError,{eventSlug: string;data: CreateEventReportRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEventReport>>,
        TError,
        {eventSlug: string;data: CreateEventReportRequest},
        TContext
      > => {

      const mutationOptions = getCreateEventReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Get a location digital twin scene with geometry and assets
 */
export const getGetLocationTwinUrl = (locationSlug: string,) => {


  

  return `/api/twin/locations/${locationSlug}`
}

export const getLocationTwin = async (locationSlug: string, options?: RequestInit): Promise<LocationTwinScene> => {
  
  return customFetch<LocationTwinScene>(getGetLocationTwinUrl(locationSlug),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetLocationTwinQueryKey = (locationSlug?: string,) => {
    return [
    `/api/twin/locations/${locationSlug}`
    ] as const;
    }

    
export const getGetLocationTwinQueryOptions = <TData = Awaited<ReturnType<typeof getLocationTwin>>, TError = NotFoundResponse | ServiceUnavailableResponse>(locationSlug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLocationTwin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLocationTwinQueryKey(locationSlug);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLocationTwin>>> = ({ signal }) => getLocationTwin(locationSlug, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(locationSlug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLocationTwin>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLocationTwinQueryResult = NonNullable<Awaited<ReturnType<typeof getLocationTwin>>>
export type GetLocationTwinQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get a location digital twin scene with geometry and assets
 */

export function useGetLocationTwin<TData = Awaited<ReturnType<typeof getLocationTwin>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 locationSlug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLocationTwin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLocationTwinQueryOptions(locationSlug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List active live-presence participants in a digital twin scene
 */
export const getListTwinPresenceUrl = (params: ListTwinPresenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/twin/presence?${stringifiedParams}` : `/api/twin/presence`
}

export const listTwinPresence = async (params: ListTwinPresenceParams, options?: RequestInit): Promise<TwinPresenceList> => {
  
  return customFetch<TwinPresenceList>(getListTwinPresenceUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListTwinPresenceQueryKey = (params?: ListTwinPresenceParams,) => {
    return [
    `/api/twin/presence`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListTwinPresenceQueryOptions = <TData = Awaited<ReturnType<typeof listTwinPresence>>, TError = BadRequestResponse>(params: ListTwinPresenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTwinPresence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTwinPresenceQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTwinPresence>>> = ({ signal }) => listTwinPresence(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTwinPresence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTwinPresenceQueryResult = NonNullable<Awaited<ReturnType<typeof listTwinPresence>>>
export type ListTwinPresenceQueryError = BadRequestResponse


/**
 * @summary List active live-presence participants in a digital twin scene
 */

export function useListTwinPresence<TData = Awaited<ReturnType<typeof listTwinPresence>>, TError = BadRequestResponse>(
 params: ListTwinPresenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTwinPresence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTwinPresenceQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Join a digital twin scene or update a participant heartbeat
 */
export const getUpdateTwinPresenceUrl = () => {


  

  return `/api/twin/presence`
}

export const updateTwinPresence = async (twinPresenceInput: TwinPresenceInput, options?: RequestInit): Promise<TwinPresenceWriteResponse> => {
  
  return customFetch<TwinPresenceWriteResponse>(getUpdateTwinPresenceUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      twinPresenceInput,)
  }
);}




export const getUpdateTwinPresenceMutationOptions = <TError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwinPresence>>, TError,{data: TwinPresenceInput}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTwinPresence>>, TError,{data: TwinPresenceInput}, TContext> => {

const mutationKey = ['updateTwinPresence'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTwinPresence>>, {data: TwinPresenceInput}> = (props) => {
          const {data} = props ?? {};

          return  updateTwinPresence(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateTwinPresenceMutationResult = NonNullable<Awaited<ReturnType<typeof updateTwinPresence>>>
    export type UpdateTwinPresenceMutationBody = TwinPresenceInput
    export type UpdateTwinPresenceMutationError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Join a digital twin scene or update a participant heartbeat
 */
export const useUpdateTwinPresence = <TError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwinPresence>>, TError,{data: TwinPresenceInput}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTwinPresence>>,
        TError,
        {data: TwinPresenceInput},
        TContext
      > => {

      const mutationOptions = getUpdateTwinPresenceMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Explicitly remove a participant from a digital twin scene
 */
export const getLeaveTwinPresenceUrl = (userId: string,
    params: LeaveTwinPresenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/twin/presence/${userId}?${stringifiedParams}` : `/api/twin/presence/${userId}`
}

export const leaveTwinPresence = async (userId: string,
    params: LeaveTwinPresenceParams, options?: RequestInit): Promise<TwinPresenceRemoval> => {
  
  return customFetch<TwinPresenceRemoval>(getLeaveTwinPresenceUrl(userId,params),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getLeaveTwinPresenceMutationOptions = <TError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof leaveTwinPresence>>, TError,{userId: string;params: LeaveTwinPresenceParams}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof leaveTwinPresence>>, TError,{userId: string;params: LeaveTwinPresenceParams}, TContext> => {

const mutationKey = ['leaveTwinPresence'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof leaveTwinPresence>>, {userId: string;params: LeaveTwinPresenceParams}> = (props) => {
          const {userId,params} = props ?? {};

          return  leaveTwinPresence(userId,params,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type LeaveTwinPresenceMutationResult = NonNullable<Awaited<ReturnType<typeof leaveTwinPresence>>>
    
    export type LeaveTwinPresenceMutationError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse

    /**
 * @summary Explicitly remove a participant from a digital twin scene
 */
export const useLeaveTwinPresence = <TError = BadRequestResponse | ForbiddenResponse | RateLimitedResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof leaveTwinPresence>>, TError,{userId: string;params: LeaveTwinPresenceParams}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof leaveTwinPresence>>,
        TError,
        {userId: string;params: LeaveTwinPresenceParams},
        TContext
      > => {

      const mutationOptions = getLeaveTwinPresenceMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Returns region boundary, buildings, roads, assets, and issues for rendering a 3D digital twin scene.
 * @summary Get all 3D scene data for a council area digital twin
 */
export const getGetTwinSceneUrl = (regionId: string,) => {


  

  return `/api/twin/${regionId}`
}

export const getTwinScene = async (regionId: string, options?: RequestInit): Promise<TwinScene> => {
  
  return customFetch<TwinScene>(getGetTwinSceneUrl(regionId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetTwinSceneQueryKey = (regionId?: string,) => {
    return [
    `/api/twin/${regionId}`
    ] as const;
    }

    
export const getGetTwinSceneQueryOptions = <TData = Awaited<ReturnType<typeof getTwinScene>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(regionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTwinScene>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTwinSceneQueryKey(regionId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTwinScene>>> = ({ signal }) => getTwinScene(regionId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(regionId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTwinScene>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTwinSceneQueryResult = NonNullable<Awaited<ReturnType<typeof getTwinScene>>>
export type GetTwinSceneQueryError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get all 3D scene data for a council area digital twin
 */

export function useGetTwinScene<TData = Awaited<ReturnType<typeof getTwinScene>>, TError = BadRequestResponse | NotFoundResponse | ServiceUnavailableResponse>(
 regionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTwinScene>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTwinSceneQueryOptions(regionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get nearby infrastructure assets with owner and open issue info (for AR overlay)
 */
export const getGetNearbyAssetsUrl = (params: GetNearbyAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/assets/nearby?${stringifiedParams}` : `/api/assets/nearby`
}

export const getNearbyAssets = async (params: GetNearbyAssetsParams, options?: RequestInit): Promise<NearbyAssetsResponse> => {
  
  return customFetch<NearbyAssetsResponse>(getGetNearbyAssetsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetNearbyAssetsQueryKey = (params?: GetNearbyAssetsParams,) => {
    return [
    `/api/assets/nearby`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetNearbyAssetsQueryOptions = <TData = Awaited<ReturnType<typeof getNearbyAssets>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params: GetNearbyAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNearbyAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNearbyAssetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNearbyAssets>>> = ({ signal }) => getNearbyAssets(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNearbyAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNearbyAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof getNearbyAssets>>>
export type GetNearbyAssetsQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary Get nearby infrastructure assets with owner and open issue info (for AR overlay)
 */

export function useGetNearbyAssets<TData = Awaited<ReturnType<typeof getNearbyAssets>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params: GetNearbyAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNearbyAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNearbyAssetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary KPI dashboard for the signed-in staff member's tenant
 */
export const getGetWorkspaceKpiUrl = () => {


  

  return `/api/workspace/kpi`
}

export const getWorkspaceKpi = async ( options?: RequestInit): Promise<WorkspaceKpi> => {
  
  return customFetch<WorkspaceKpi>(getGetWorkspaceKpiUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceKpiQueryKey = () => {
    return [
    `/api/workspace/kpi`
    ] as const;
    }

    
export const getGetWorkspaceKpiQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceKpi>>, TError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceKpi>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceKpiQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceKpi>>> = ({ signal }) => getWorkspaceKpi({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceKpi>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceKpiQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceKpi>>>
export type GetWorkspaceKpiQueryError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse


/**
 * @summary KPI dashboard for the signed-in staff member's tenant
 */

export function useGetWorkspaceKpi<TData = Awaited<ReturnType<typeof getWorkspaceKpi>>, TError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceKpi>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceKpiQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List staff users in the tenant for the assignment picker
 */
export const getGetWorkspaceStaffUrl = () => {


  

  return `/api/workspace/staff`
}

export const getWorkspaceStaff = async ( options?: RequestInit): Promise<WorkspaceStaffList> => {
  
  return customFetch<WorkspaceStaffList>(getGetWorkspaceStaffUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceStaffQueryKey = () => {
    return [
    `/api/workspace/staff`
    ] as const;
    }

    
export const getGetWorkspaceStaffQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceStaff>>, TError = UnauthenticatedResponse | ForbiddenResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceStaff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceStaffQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceStaff>>> = ({ signal }) => getWorkspaceStaff({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceStaff>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceStaffQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceStaff>>>
export type GetWorkspaceStaffQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List staff users in the tenant for the assignment picker
 */

export function useGetWorkspaceStaff<TData = Awaited<ReturnType<typeof getWorkspaceStaff>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceStaff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceStaffQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Tenant-scoped triage queue with filters and sorting
 */
export const getGetWorkspaceReportsUrl = (params?: GetWorkspaceReportsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspace/reports?${stringifiedParams}` : `/api/workspace/reports`
}

export const getWorkspaceReports = async (params?: GetWorkspaceReportsParams, options?: RequestInit): Promise<WorkspaceReportList> => {
  
  return customFetch<WorkspaceReportList>(getGetWorkspaceReportsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceReportsQueryKey = (params?: GetWorkspaceReportsParams,) => {
    return [
    `/api/workspace/reports`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetWorkspaceReportsQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceReports>>, TError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse>(params?: GetWorkspaceReportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceReportsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceReports>>> = ({ signal }) => getWorkspaceReports(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReports>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceReportsQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceReports>>>
export type GetWorkspaceReportsQueryError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse


/**
 * @summary Tenant-scoped triage queue with filters and sorting
 */

export function useGetWorkspaceReports<TData = Awaited<ReturnType<typeof getWorkspaceReports>>, TError = UnauthenticatedResponse | ForbiddenResponse | ServiceUnavailableResponse>(
 params?: GetWorkspaceReportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceReportsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Full report detail with timeline, comments, assignments, and notifications
 */
export const getGetWorkspaceReportDetailUrl = (reportId: string,) => {


  

  return `/api/workspace/reports/${reportId}`
}

export const getWorkspaceReportDetail = async (reportId: string, options?: RequestInit): Promise<WorkspaceReportDetail> => {
  
  return customFetch<WorkspaceReportDetail>(getGetWorkspaceReportDetailUrl(reportId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceReportDetailQueryKey = (reportId?: string,) => {
    return [
    `/api/workspace/reports/${reportId}`
    ] as const;
    }

    
export const getGetWorkspaceReportDetailQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceReportDetail>>, TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse>(reportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReportDetail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceReportDetailQueryKey(reportId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceReportDetail>>> = ({ signal }) => getWorkspaceReportDetail(reportId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(reportId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReportDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceReportDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceReportDetail>>>
export type GetWorkspaceReportDetailQueryError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse


/**
 * @summary Full report detail with timeline, comments, assignments, and notifications
 */

export function useGetWorkspaceReportDetail<TData = Awaited<ReturnType<typeof getWorkspaceReportDetail>>, TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse>(
 reportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceReportDetail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceReportDetailQueryOptions(reportId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Assign or claim a report for a staff member
 */
export const getAssignWorkspaceReportUrl = (reportId: string,) => {


  

  return `/api/workspace/reports/${reportId}/assign`
}

export const assignWorkspaceReport = async (reportId: string,
    assignWorkspaceReportBody: AssignWorkspaceReportBody, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getAssignWorkspaceReportUrl(reportId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      assignWorkspaceReportBody,)
  }
);}




export const getAssignWorkspaceReportMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignWorkspaceReport>>, TError,{reportId: string;data: AssignWorkspaceReportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof assignWorkspaceReport>>, TError,{reportId: string;data: AssignWorkspaceReportBody}, TContext> => {

const mutationKey = ['assignWorkspaceReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof assignWorkspaceReport>>, {reportId: string;data: AssignWorkspaceReportBody}> = (props) => {
          const {reportId,data} = props ?? {};

          return  assignWorkspaceReport(reportId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type AssignWorkspaceReportMutationResult = NonNullable<Awaited<ReturnType<typeof assignWorkspaceReport>>>
    export type AssignWorkspaceReportMutationBody = AssignWorkspaceReportBody
    export type AssignWorkspaceReportMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Assign or claim a report for a staff member
 */
export const useAssignWorkspaceReport = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignWorkspaceReport>>, TError,{reportId: string;data: AssignWorkspaceReportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof assignWorkspaceReport>>,
        TError,
        {reportId: string;data: AssignWorkspaceReportBody},
        TContext
      > => {

      const mutationOptions = getAssignWorkspaceReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Transition a report to a new lifecycle status with a required public note
 */
export const getTransitionWorkspaceReportUrl = (reportId: string,) => {


  

  return `/api/workspace/reports/${reportId}/transition`
}

export const transitionWorkspaceReport = async (reportId: string,
    transitionRequest: TransitionRequest, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getTransitionWorkspaceReportUrl(reportId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      transitionRequest,)
  }
);}




export const getTransitionWorkspaceReportMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionWorkspaceReport>>, TError,{reportId: string;data: TransitionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof transitionWorkspaceReport>>, TError,{reportId: string;data: TransitionRequest}, TContext> => {

const mutationKey = ['transitionWorkspaceReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof transitionWorkspaceReport>>, {reportId: string;data: TransitionRequest}> = (props) => {
          const {reportId,data} = props ?? {};

          return  transitionWorkspaceReport(reportId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type TransitionWorkspaceReportMutationResult = NonNullable<Awaited<ReturnType<typeof transitionWorkspaceReport>>>
    export type TransitionWorkspaceReportMutationBody = TransitionRequest
    export type TransitionWorkspaceReportMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Transition a report to a new lifecycle status with a required public note
 */
export const useTransitionWorkspaceReport = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionWorkspaceReport>>, TError,{reportId: string;data: TransitionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof transitionWorkspaceReport>>,
        TError,
        {reportId: string;data: TransitionRequest},
        TContext
      > => {

      const mutationOptions = getTransitionWorkspaceReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Add an internal or public comment to a report
 */
export const getAddWorkspaceCommentUrl = (reportId: string,) => {


  

  return `/api/workspace/reports/${reportId}/comments`
}

export const addWorkspaceComment = async (reportId: string,
    commentRequest: CommentRequest, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getAddWorkspaceCommentUrl(reportId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      commentRequest,)
  }
);}




export const getAddWorkspaceCommentMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceComment>>, TError,{reportId: string;data: CommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceComment>>, TError,{reportId: string;data: CommentRequest}, TContext> => {

const mutationKey = ['addWorkspaceComment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addWorkspaceComment>>, {reportId: string;data: CommentRequest}> = (props) => {
          const {reportId,data} = props ?? {};

          return  addWorkspaceComment(reportId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type AddWorkspaceCommentMutationResult = NonNullable<Awaited<ReturnType<typeof addWorkspaceComment>>>
    export type AddWorkspaceCommentMutationBody = CommentRequest
    export type AddWorkspaceCommentMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Add an internal or public comment to a report
 */
export const useAddWorkspaceComment = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceComment>>, TError,{reportId: string;data: CommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addWorkspaceComment>>,
        TError,
        {reportId: string;data: CommentRequest},
        TContext
      > => {

      const mutationOptions = getAddWorkspaceCommentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Mark a report as duplicate and transfer upvotes to the canonical report
 */
export const getMergeWorkspaceReportUrl = (reportId: string,) => {


  

  return `/api/workspace/reports/${reportId}/merge`
}

export const mergeWorkspaceReport = async (reportId: string,
    mergeWorkspaceReportBody: MergeWorkspaceReportBody, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getMergeWorkspaceReportUrl(reportId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      mergeWorkspaceReportBody,)
  }
);}




export const getMergeWorkspaceReportMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof mergeWorkspaceReport>>, TError,{reportId: string;data: MergeWorkspaceReportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof mergeWorkspaceReport>>, TError,{reportId: string;data: MergeWorkspaceReportBody}, TContext> => {

const mutationKey = ['mergeWorkspaceReport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof mergeWorkspaceReport>>, {reportId: string;data: MergeWorkspaceReportBody}> = (props) => {
          const {reportId,data} = props ?? {};

          return  mergeWorkspaceReport(reportId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MergeWorkspaceReportMutationResult = NonNullable<Awaited<ReturnType<typeof mergeWorkspaceReport>>>
    export type MergeWorkspaceReportMutationBody = MergeWorkspaceReportBody
    export type MergeWorkspaceReportMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Mark a report as duplicate and transfer upvotes to the canonical report
 */
export const useMergeWorkspaceReport = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof mergeWorkspaceReport>>, TError,{reportId: string;data: MergeWorkspaceReportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof mergeWorkspaceReport>>,
        TError,
        {reportId: string;data: MergeWorkspaceReportBody},
        TContext
      > => {

      const mutationOptions = getMergeWorkspaceReportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List assets for the tenant
 */
export const getGetWorkspaceAssetsUrl = (params?: GetWorkspaceAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspace/assets?${stringifiedParams}` : `/api/workspace/assets`
}

export const getWorkspaceAssets = async (params?: GetWorkspaceAssetsParams, options?: RequestInit): Promise<WorkspaceAssetList> => {
  
  return customFetch<WorkspaceAssetList>(getGetWorkspaceAssetsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceAssetsQueryKey = (params?: GetWorkspaceAssetsParams,) => {
    return [
    `/api/workspace/assets`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetWorkspaceAssetsQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceAssets>>, TError = UnauthenticatedResponse | ForbiddenResponse>(params?: GetWorkspaceAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceAssetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceAssets>>> = ({ signal }) => getWorkspaceAssets(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceAssets>>>
export type GetWorkspaceAssetsQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List assets for the tenant
 */

export function useGetWorkspaceAssets<TData = Awaited<ReturnType<typeof getWorkspaceAssets>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
 params?: GetWorkspaceAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceAssetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create a new asset (admin only)
 */
export const getCreateWorkspaceAssetUrl = () => {


  

  return `/api/workspace/assets`
}

export const createWorkspaceAsset = async (createAssetRequest: CreateAssetRequest, options?: RequestInit): Promise<CreateWorkspaceAsset201> => {
  
  return customFetch<CreateWorkspaceAsset201>(getCreateWorkspaceAssetUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createAssetRequest,)
  }
);}




export const getCreateWorkspaceAssetMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAsset>>, TError,{data: CreateAssetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAsset>>, TError,{data: CreateAssetRequest}, TContext> => {

const mutationKey = ['createWorkspaceAsset'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkspaceAsset>>, {data: CreateAssetRequest}> = (props) => {
          const {data} = props ?? {};

          return  createWorkspaceAsset(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkspaceAssetMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkspaceAsset>>>
    export type CreateWorkspaceAssetMutationBody = CreateAssetRequest
    export type CreateWorkspaceAssetMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Create a new asset (admin only)
 */
export const useCreateWorkspaceAsset = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAsset>>, TError,{data: CreateAssetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWorkspaceAsset>>,
        TError,
        {data: CreateAssetRequest},
        TContext
      > => {

      const mutationOptions = getCreateWorkspaceAssetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update an asset (admin only)
 */
export const getUpdateWorkspaceAssetUrl = (assetId: string,) => {


  

  return `/api/workspace/assets/${assetId}`
}

export const updateWorkspaceAsset = async (assetId: string,
    updateAssetRequest: UpdateAssetRequest, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getUpdateWorkspaceAssetUrl(assetId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateAssetRequest,)
  }
);}




export const getUpdateWorkspaceAssetMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAsset>>, TError,{assetId: string;data: UpdateAssetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAsset>>, TError,{assetId: string;data: UpdateAssetRequest}, TContext> => {

const mutationKey = ['updateWorkspaceAsset'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspaceAsset>>, {assetId: string;data: UpdateAssetRequest}> = (props) => {
          const {assetId,data} = props ?? {};

          return  updateWorkspaceAsset(assetId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceAssetMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspaceAsset>>>
    export type UpdateWorkspaceAssetMutationBody = UpdateAssetRequest
    export type UpdateWorkspaceAssetMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Update an asset (admin only)
 */
export const useUpdateWorkspaceAsset = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAsset>>, TError,{assetId: string;data: UpdateAssetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspaceAsset>>,
        TError,
        {assetId: string;data: UpdateAssetRequest},
        TContext
      > => {

      const mutationOptions = getUpdateWorkspaceAssetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Retire an asset (admin only)
 */
export const getRetireWorkspaceAssetUrl = (assetId: string,) => {


  

  return `/api/workspace/assets/${assetId}/retire`
}

export const retireWorkspaceAsset = async (assetId: string, options?: RequestInit): Promise<RetireWorkspaceAsset200> => {
  
  return customFetch<RetireWorkspaceAsset200>(getRetireWorkspaceAssetUrl(assetId),
  {      
    ...options,
    method: 'PATCH'
    
    
  }
);}




export const getRetireWorkspaceAssetMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retireWorkspaceAsset>>, TError,{assetId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retireWorkspaceAsset>>, TError,{assetId: string}, TContext> => {

const mutationKey = ['retireWorkspaceAsset'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retireWorkspaceAsset>>, {assetId: string}> = (props) => {
          const {assetId} = props ?? {};

          return  retireWorkspaceAsset(assetId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type RetireWorkspaceAssetMutationResult = NonNullable<Awaited<ReturnType<typeof retireWorkspaceAsset>>>
    
    export type RetireWorkspaceAssetMutationError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Retire an asset (admin only)
 */
export const useRetireWorkspaceAsset = <TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retireWorkspaceAsset>>, TError,{assetId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retireWorkspaceAsset>>,
        TError,
        {assetId: string},
        TContext
      > => {

      const mutationOptions = getRetireWorkspaceAssetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Bulk retire assets (admin only)
 */
export const getBulkRetireWorkspaceAssetsUrl = () => {


  

  return `/api/workspace/assets/bulk-retire`
}

export const bulkRetireWorkspaceAssets = async (bulkRetireWorkspaceAssetsBody: BulkRetireWorkspaceAssetsBody, options?: RequestInit): Promise<BulkRetireWorkspaceAssets200> => {
  
  return customFetch<BulkRetireWorkspaceAssets200>(getBulkRetireWorkspaceAssetsUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      bulkRetireWorkspaceAssetsBody,)
  }
);}




export const getBulkRetireWorkspaceAssetsMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>, TError,{data: BulkRetireWorkspaceAssetsBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>, TError,{data: BulkRetireWorkspaceAssetsBody}, TContext> => {

const mutationKey = ['bulkRetireWorkspaceAssets'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>, {data: BulkRetireWorkspaceAssetsBody}> = (props) => {
          const {data} = props ?? {};

          return  bulkRetireWorkspaceAssets(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type BulkRetireWorkspaceAssetsMutationResult = NonNullable<Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>>
    export type BulkRetireWorkspaceAssetsMutationBody = BulkRetireWorkspaceAssetsBody
    export type BulkRetireWorkspaceAssetsMutationError = UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Bulk retire assets (admin only)
 */
export const useBulkRetireWorkspaceAssets = <TError = UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>, TError,{data: BulkRetireWorkspaceAssetsBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof bulkRetireWorkspaceAssets>>,
        TError,
        {data: BulkRetireWorkspaceAssetsBody},
        TContext
      > => {

      const mutationOptions = getBulkRetireWorkspaceAssetsMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List all asset types
 */
export const getGetWorkspaceAssetTypesUrl = () => {


  

  return `/api/workspace/asset-types`
}

export const getWorkspaceAssetTypes = async ( options?: RequestInit): Promise<WorkspaceAssetTypeList> => {
  
  return customFetch<WorkspaceAssetTypeList>(getGetWorkspaceAssetTypesUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceAssetTypesQueryKey = () => {
    return [
    `/api/workspace/asset-types`
    ] as const;
    }

    
export const getGetWorkspaceAssetTypesQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceAssetTypes>>, TError = UnauthenticatedResponse | ForbiddenResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssetTypes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceAssetTypesQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceAssetTypes>>> = ({ signal }) => getWorkspaceAssetTypes({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssetTypes>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceAssetTypesQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceAssetTypes>>>
export type GetWorkspaceAssetTypesQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List all asset types
 */

export function useGetWorkspaceAssetTypes<TData = Awaited<ReturnType<typeof getWorkspaceAssetTypes>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceAssetTypes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceAssetTypesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create an asset type (admin only)
 */
export const getCreateWorkspaceAssetTypeUrl = () => {


  

  return `/api/workspace/asset-types`
}

export const createWorkspaceAssetType = async (createAssetTypeRequest: CreateAssetTypeRequest, options?: RequestInit): Promise<CreateWorkspaceAssetType201> => {
  
  return customFetch<CreateWorkspaceAssetType201>(getCreateWorkspaceAssetTypeUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createAssetTypeRequest,)
  }
);}




export const getCreateWorkspaceAssetTypeMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAssetType>>, TError,{data: CreateAssetTypeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAssetType>>, TError,{data: CreateAssetTypeRequest}, TContext> => {

const mutationKey = ['createWorkspaceAssetType'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkspaceAssetType>>, {data: CreateAssetTypeRequest}> = (props) => {
          const {data} = props ?? {};

          return  createWorkspaceAssetType(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkspaceAssetTypeMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkspaceAssetType>>>
    export type CreateWorkspaceAssetTypeMutationBody = CreateAssetTypeRequest
    export type CreateWorkspaceAssetTypeMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Create an asset type (admin only)
 */
export const useCreateWorkspaceAssetType = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceAssetType>>, TError,{data: CreateAssetTypeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWorkspaceAssetType>>,
        TError,
        {data: CreateAssetTypeRequest},
        TContext
      > => {

      const mutationOptions = getCreateWorkspaceAssetTypeMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update an asset type (admin only)
 */
export const getUpdateWorkspaceAssetTypeUrl = (typeId: string,) => {


  

  return `/api/workspace/asset-types/${typeId}`
}

export const updateWorkspaceAssetType = async (typeId: string,
    updateAssetTypeRequest: UpdateAssetTypeRequest, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getUpdateWorkspaceAssetTypeUrl(typeId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateAssetTypeRequest,)
  }
);}




export const getUpdateWorkspaceAssetTypeMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAssetType>>, TError,{typeId: string;data: UpdateAssetTypeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAssetType>>, TError,{typeId: string;data: UpdateAssetTypeRequest}, TContext> => {

const mutationKey = ['updateWorkspaceAssetType'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspaceAssetType>>, {typeId: string;data: UpdateAssetTypeRequest}> = (props) => {
          const {typeId,data} = props ?? {};

          return  updateWorkspaceAssetType(typeId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceAssetTypeMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspaceAssetType>>>
    export type UpdateWorkspaceAssetTypeMutationBody = UpdateAssetTypeRequest
    export type UpdateWorkspaceAssetTypeMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Update an asset type (admin only)
 */
export const useUpdateWorkspaceAssetType = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceAssetType>>, TError,{typeId: string;data: UpdateAssetTypeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspaceAssetType>>,
        TError,
        {typeId: string;data: UpdateAssetTypeRequest},
        TContext
      > => {

      const mutationOptions = getUpdateWorkspaceAssetTypeMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List tenant regions with boundaries
 */
export const getGetWorkspaceRegionsUrl = () => {


  

  return `/api/workspace/regions`
}

export const getWorkspaceRegions = async ( options?: RequestInit): Promise<WorkspaceRegionList> => {
  
  return customFetch<WorkspaceRegionList>(getGetWorkspaceRegionsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetWorkspaceRegionsQueryKey = () => {
    return [
    `/api/workspace/regions`
    ] as const;
    }

    
export const getGetWorkspaceRegionsQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceRegions>>, TError = UnauthenticatedResponse | ForbiddenResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceRegions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceRegionsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceRegions>>> = ({ signal }) => getWorkspaceRegions({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceRegions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWorkspaceRegionsQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceRegions>>>
export type GetWorkspaceRegionsQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List tenant regions with boundaries
 */

export function useGetWorkspaceRegions<TData = Awaited<ReturnType<typeof getWorkspaceRegions>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceRegions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWorkspaceRegionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Update a region boundary (admin only, validated)
 */
export const getUpdateWorkspaceRegionBoundaryUrl = (regionId: string,) => {


  

  return `/api/workspace/regions/${regionId}/boundary`
}

export const updateWorkspaceRegionBoundary = async (regionId: string,
    updateWorkspaceRegionBoundaryBody: UpdateWorkspaceRegionBoundaryBody, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getUpdateWorkspaceRegionBoundaryUrl(regionId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateWorkspaceRegionBoundaryBody,)
  }
);}




export const getUpdateWorkspaceRegionBoundaryMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>, TError,{regionId: string;data: UpdateWorkspaceRegionBoundaryBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>, TError,{regionId: string;data: UpdateWorkspaceRegionBoundaryBody}, TContext> => {

const mutationKey = ['updateWorkspaceRegionBoundary'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>, {regionId: string;data: UpdateWorkspaceRegionBoundaryBody}> = (props) => {
          const {regionId,data} = props ?? {};

          return  updateWorkspaceRegionBoundary(regionId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceRegionBoundaryMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>>
    export type UpdateWorkspaceRegionBoundaryMutationBody = UpdateWorkspaceRegionBoundaryBody
    export type UpdateWorkspaceRegionBoundaryMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Update a region boundary (admin only, validated)
 */
export const useUpdateWorkspaceRegionBoundary = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>, TError,{regionId: string;data: UpdateWorkspaceRegionBoundaryBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspaceRegionBoundary>>,
        TError,
        {regionId: string;data: UpdateWorkspaceRegionBoundaryBody},
        TContext
      > => {

      const mutationOptions = getUpdateWorkspaceRegionBoundaryMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Upload a GeoJSON or CSV file for import (admin only)
 */
export const getUploadImportFileUrl = () => {


  

  return `/api/workspace/assets/import/upload`
}

export const uploadImportFile = async (uploadImportFileBody: UploadImportFileBody, options?: RequestInit): Promise<ImportUploadResponse> => {
    const formData = new FormData();
if(uploadImportFileBody.file !== undefined) {
 formData.append(`file`, uploadImportFileBody.file)
 }

  return customFetch<ImportUploadResponse>(getUploadImportFileUrl(),
  {      
    ...options,
    method: 'POST'
    ,
    body: 
      formData,
  }
);}




export const getUploadImportFileMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadImportFile>>, TError,{data: UploadImportFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadImportFile>>, TError,{data: UploadImportFileBody}, TContext> => {

const mutationKey = ['uploadImportFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadImportFile>>, {data: UploadImportFileBody}> = (props) => {
          const {data} = props ?? {};

          return  uploadImportFile(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UploadImportFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadImportFile>>>
    export type UploadImportFileMutationBody = UploadImportFileBody
    export type UploadImportFileMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Upload a GeoJSON or CSV file for import (admin only)
 */
export const useUploadImportFile = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadImportFile>>, TError,{data: UploadImportFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadImportFile>>,
        TError,
        {data: UploadImportFileBody},
        TContext
      > => {

      const mutationOptions = getUploadImportFileMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Validate imported data with column mapping (admin only)
 */
export const getPreviewImportUrl = () => {


  

  return `/api/workspace/assets/import/preview`
}

export const previewImport = async (importPreviewRequest: ImportPreviewRequest, options?: RequestInit): Promise<ImportPreviewResponse> => {
  
  return customFetch<ImportPreviewResponse>(getPreviewImportUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      importPreviewRequest,)
  }
);}




export const getPreviewImportMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewImport>>, TError,{data: ImportPreviewRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewImport>>, TError,{data: ImportPreviewRequest}, TContext> => {

const mutationKey = ['previewImport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewImport>>, {data: ImportPreviewRequest}> = (props) => {
          const {data} = props ?? {};

          return  previewImport(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type PreviewImportMutationResult = NonNullable<Awaited<ReturnType<typeof previewImport>>>
    export type PreviewImportMutationBody = ImportPreviewRequest
    export type PreviewImportMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Validate imported data with column mapping (admin only)
 */
export const usePreviewImport = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewImport>>, TError,{data: ImportPreviewRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewImport>>,
        TError,
        {data: ImportPreviewRequest},
        TContext
      > => {

      const mutationOptions = getPreviewImportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Commit the import, upserting by external_source/external_id (admin only)
 */
export const getCommitImportUrl = () => {


  

  return `/api/workspace/assets/import/commit`
}

export const commitImport = async (commitImportBody: CommitImportBody, options?: RequestInit): Promise<ImportCommitResponse> => {
  
  return customFetch<ImportCommitResponse>(getCommitImportUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      commitImportBody,)
  }
);}




export const getCommitImportMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitImport>>, TError,{data: CommitImportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof commitImport>>, TError,{data: CommitImportBody}, TContext> => {

const mutationKey = ['commitImport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof commitImport>>, {data: CommitImportBody}> = (props) => {
          const {data} = props ?? {};

          return  commitImport(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CommitImportMutationResult = NonNullable<Awaited<ReturnType<typeof commitImport>>>
    export type CommitImportMutationBody = CommitImportBody
    export type CommitImportMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse

    /**
 * @summary Commit the import, upserting by external_source/external_id (admin only)
 */
export const useCommitImport = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitImport>>, TError,{data: CommitImportBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof commitImport>>,
        TError,
        {data: CommitImportBody},
        TContext
      > => {

      const mutationOptions = getCommitImportMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List past import runs
 */
export const getGetImportHistoryUrl = (params?: GetImportHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspace/assets/import/history?${stringifiedParams}` : `/api/workspace/assets/import/history`
}

export const getImportHistory = async (params?: GetImportHistoryParams, options?: RequestInit): Promise<ImportHistoryList> => {
  
  return customFetch<ImportHistoryList>(getGetImportHistoryUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetImportHistoryQueryKey = (params?: GetImportHistoryParams,) => {
    return [
    `/api/workspace/assets/import/history`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetImportHistoryQueryOptions = <TData = Awaited<ReturnType<typeof getImportHistory>>, TError = UnauthenticatedResponse | ForbiddenResponse>(params?: GetImportHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getImportHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetImportHistoryQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getImportHistory>>> = ({ signal }) => getImportHistory(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getImportHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type GetImportHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getImportHistory>>>
export type GetImportHistoryQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List past import runs
 */

export function useGetImportHistory<TData = Awaited<ReturnType<typeof getImportHistory>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
 params?: GetImportHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getImportHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetImportHistoryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Download error report as CSV
 */
export const getGetImportErrorsUrl = (runId: string,) => {


  

  return `/api/workspace/assets/import/${runId}/errors`
}

export const getImportErrors = async (runId: string, options?: RequestInit): Promise<string> => {
  
  return customFetch<string>(getGetImportErrorsUrl(runId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetImportErrorsQueryKey = (runId?: string,) => {
    return [
    `/api/workspace/assets/import/${runId}/errors`
    ] as const;
    }

    
export const getGetImportErrorsQueryOptions = <TData = Awaited<ReturnType<typeof getImportErrors>>, TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse>(runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getImportErrors>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetImportErrorsQueryKey(runId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getImportErrors>>> = ({ signal }) => getImportErrors(runId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(runId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getImportErrors>>, TError, TData> & { queryKey: QueryKey }
}

export type GetImportErrorsQueryResult = NonNullable<Awaited<ReturnType<typeof getImportErrors>>>
export type GetImportErrorsQueryError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse


/**
 * @summary Download error report as CSV
 */

export function useGetImportErrors<TData = Awaited<ReturnType<typeof getImportErrors>>, TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse>(
 runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getImportErrors>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetImportErrorsQueryOptions(runId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Returns all council tenants with open report counts for the council selector.
Only the service administrator (super_admin role) may call this endpoint.

 * @summary List all council tenants available for switching (super_admin only)
 */
export const getGetSwitchableCouncilsUrl = () => {


  

  return `/api/workspace/switchable-councils`
}

export const getSwitchableCouncils = async ( options?: RequestInit): Promise<SwitchableCouncilList> => {
  
  return customFetch<SwitchableCouncilList>(getGetSwitchableCouncilsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetSwitchableCouncilsQueryKey = () => {
    return [
    `/api/workspace/switchable-councils`
    ] as const;
    }

    
export const getGetSwitchableCouncilsQueryOptions = <TData = Awaited<ReturnType<typeof getSwitchableCouncils>>, TError = UnauthenticatedResponse | ForbiddenResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSwitchableCouncils>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSwitchableCouncilsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSwitchableCouncils>>> = ({ signal }) => getSwitchableCouncils({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSwitchableCouncils>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSwitchableCouncilsQueryResult = NonNullable<Awaited<ReturnType<typeof getSwitchableCouncils>>>
export type GetSwitchableCouncilsQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List all council tenants available for switching (super_admin only)
 */

export function useGetSwitchableCouncils<TData = Awaited<ReturnType<typeof getSwitchableCouncils>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSwitchableCouncils>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSwitchableCouncilsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Returns pending custom issue-type suggestions submitted by anonymous
reporters, with originating report context for council staff review.

 * @summary List pending issue-type suggestions
 */
export const getListCategorySuggestionsUrl = () => {


  

  return `/api/workspace/suggestions`
}

export const listCategorySuggestions = async ( options?: RequestInit): Promise<CategorySuggestionList> => {
  
  return customFetch<CategorySuggestionList>(getListCategorySuggestionsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListCategorySuggestionsQueryKey = () => {
    return [
    `/api/workspace/suggestions`
    ] as const;
    }

    
export const getListCategorySuggestionsQueryOptions = <TData = Awaited<ReturnType<typeof listCategorySuggestions>>, TError = UnauthenticatedResponse | ForbiddenResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCategorySuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCategorySuggestionsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCategorySuggestions>>> = ({ signal }) => listCategorySuggestions({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCategorySuggestions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCategorySuggestionsQueryResult = NonNullable<Awaited<ReturnType<typeof listCategorySuggestions>>>
export type ListCategorySuggestionsQueryError = UnauthenticatedResponse | ForbiddenResponse


/**
 * @summary List pending issue-type suggestions
 */

export function useListCategorySuggestions<TData = Awaited<ReturnType<typeof listCategorySuggestions>>, TError = UnauthenticatedResponse | ForbiddenResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCategorySuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCategorySuggestionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Creates a new approved `issue_categories` row from the suggestion label
(optionally mapped to an asset type) and marks the suggestion as approved.
The new category appears in the public category list for future reporters.

 * @summary Approve a pending issue-type suggestion
 */
export const getApproveCategorySuggestionUrl = (suggestionId: string,) => {


  

  return `/api/workspace/suggestions/${suggestionId}/approve`
}

export const approveCategorySuggestion = async (suggestionId: string,
    approveCategorySuggestionBody?: ApproveCategorySuggestionBody, options?: RequestInit): Promise<CategorySuggestion> => {
  
  return customFetch<CategorySuggestion>(getApproveCategorySuggestionUrl(suggestionId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      approveCategorySuggestionBody,)
  }
);}




export const getApproveCategorySuggestionMutationOptions = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveCategorySuggestion>>, TError,{suggestionId: string;data: ApproveCategorySuggestionBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveCategorySuggestion>>, TError,{suggestionId: string;data: ApproveCategorySuggestionBody}, TContext> => {

const mutationKey = ['approveCategorySuggestion'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveCategorySuggestion>>, {suggestionId: string;data: ApproveCategorySuggestionBody}> = (props) => {
          const {suggestionId,data} = props ?? {};

          return  approveCategorySuggestion(suggestionId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type ApproveCategorySuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof approveCategorySuggestion>>>
    export type ApproveCategorySuggestionMutationBody = ApproveCategorySuggestionBody
    export type ApproveCategorySuggestionMutationError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Approve a pending issue-type suggestion
 */
export const useApproveCategorySuggestion = <TError = BadRequestResponse | UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveCategorySuggestion>>, TError,{suggestionId: string;data: ApproveCategorySuggestionBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveCategorySuggestion>>,
        TError,
        {suggestionId: string;data: ApproveCategorySuggestionBody},
        TContext
      > => {

      const mutationOptions = getApproveCategorySuggestionMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Marks the suggestion as rejected. The original report keeps its label
and Other category. No new category is created.

 * @summary Reject a pending issue-type suggestion
 */
export const getRejectCategorySuggestionUrl = (suggestionId: string,) => {


  

  return `/api/workspace/suggestions/${suggestionId}/reject`
}

export const rejectCategorySuggestion = async (suggestionId: string, options?: RequestInit): Promise<CategorySuggestion> => {
  
  return customFetch<CategorySuggestion>(getRejectCategorySuggestionUrl(suggestionId),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getRejectCategorySuggestionMutationOptions = <TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectCategorySuggestion>>, TError,{suggestionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectCategorySuggestion>>, TError,{suggestionId: string}, TContext> => {

const mutationKey = ['rejectCategorySuggestion'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectCategorySuggestion>>, {suggestionId: string}> = (props) => {
          const {suggestionId} = props ?? {};

          return  rejectCategorySuggestion(suggestionId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type RejectCategorySuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof rejectCategorySuggestion>>>
    
    export type RejectCategorySuggestionMutationError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse

    /**
 * @summary Reject a pending issue-type suggestion
 */
export const useRejectCategorySuggestion = <TError = UnauthenticatedResponse | ForbiddenResponse | NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectCategorySuggestion>>, TError,{suggestionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectCategorySuggestion>>,
        TError,
        {suggestionId: string},
        TContext
      > => {

      const mutationOptions = getRejectCategorySuggestionMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Proxies a free-text address search to OpenStreetMap Nominatim,
biased to Victoria, Australia. Results are rate-limited per IP.
The browser never calls Nominatim directly.

 * @summary Search for addresses (Victoria, Australia biased)
 */
export const getGetGeocodeSearchUrl = (params: GetGeocodeSearchParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geocode/search?${stringifiedParams}` : `/api/geocode/search`
}

export const getGeocodeSearch = async (params: GetGeocodeSearchParams, options?: RequestInit): Promise<GeocodeSearchResponse> => {
  
  return customFetch<GeocodeSearchResponse>(getGetGeocodeSearchUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetGeocodeSearchQueryKey = (params?: GetGeocodeSearchParams,) => {
    return [
    `/api/geocode/search`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetGeocodeSearchQueryOptions = <TData = Awaited<ReturnType<typeof getGeocodeSearch>>, TError = Error>(params: GetGeocodeSearchParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeocodeSearch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGeocodeSearchQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGeocodeSearch>>> = ({ signal }) => getGeocodeSearch(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGeocodeSearch>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGeocodeSearchQueryResult = NonNullable<Awaited<ReturnType<typeof getGeocodeSearch>>>
export type GetGeocodeSearchQueryError = Error


/**
 * @summary Search for addresses (Victoria, Australia biased)
 */

export function useGetGeocodeSearch<TData = Awaited<ReturnType<typeof getGeocodeSearch>>, TError = Error>(
 params: GetGeocodeSearchParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeocodeSearch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGeocodeSearchQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Returns active and/or closed treasure hunts for discovery on the public treasure-hunt landing page. Draft hunts are never included. Defaults to active hunts only.

 * @summary List available treasure hunts (public, no auth)
 */
export const getListPublicHuntsUrl = (params?: ListPublicHuntsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/hunts?${stringifiedParams}` : `/api/hunts`
}

export const listPublicHunts = async (params?: ListPublicHuntsParams, options?: RequestInit): Promise<HuntListResult> => {
  
  return customFetch<HuntListResult>(getListPublicHuntsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListPublicHuntsQueryKey = (params?: ListPublicHuntsParams,) => {
    return [
    `/api/hunts`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListPublicHuntsQueryOptions = <TData = Awaited<ReturnType<typeof listPublicHunts>>, TError = BadRequestResponse | ServiceUnavailableResponse>(params?: ListPublicHuntsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicHunts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPublicHuntsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPublicHunts>>> = ({ signal }) => listPublicHunts(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPublicHunts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPublicHuntsQueryResult = NonNullable<Awaited<ReturnType<typeof listPublicHunts>>>
export type ListPublicHuntsQueryError = BadRequestResponse | ServiceUnavailableResponse


/**
 * @summary List available treasure hunts (public, no auth)
 */

export function useListPublicHunts<TData = Awaited<ReturnType<typeof listPublicHunts>>, TError = BadRequestResponse | ServiceUnavailableResponse>(
 params?: ListPublicHuntsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicHunts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPublicHuntsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Get hunt state, stations, and challenges (no secret codes)
 */
export const getGetHuntUrl = (slug: string,) => {


  

  return `/api/hunts/${slug}`
}

export const getHunt = async (slug: string, options?: RequestInit): Promise<HuntState> => {
  
  return customFetch<HuntState>(getGetHuntUrl(slug),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetHuntQueryKey = (slug?: string,) => {
    return [
    `/api/hunts/${slug}`
    ] as const;
    }

    
export const getGetHuntQueryOptions = <TData = Awaited<ReturnType<typeof getHunt>>, TError = NotFoundResponse | ServiceUnavailableResponse>(slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHunt>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHuntQueryKey(slug);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHunt>>> = ({ signal }) => getHunt(slug, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(slug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHunt>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHuntQueryResult = NonNullable<Awaited<ReturnType<typeof getHunt>>>
export type GetHuntQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get hunt state, stations, and challenges (no secret codes)
 */

export function useGetHunt<TData = Awaited<ReturnType<typeof getHunt>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHunt>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHuntQueryOptions(slug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Join a hunt with a nickname and optional team (rate-limited)
 */
export const getJoinHuntUrl = (slug: string,) => {


  

  return `/api/hunts/${slug}/join`
}

export const joinHunt = async (slug: string,
    joinHuntRequest: JoinHuntRequest, options?: RequestInit): Promise<JoinHuntResult> => {
  
  return customFetch<JoinHuntResult>(getJoinHuntUrl(slug),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      joinHuntRequest,)
  }
);}




export const getJoinHuntMutationOptions = <TError = BadRequestResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof joinHunt>>, TError,{slug: string;data: JoinHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof joinHunt>>, TError,{slug: string;data: JoinHuntRequest}, TContext> => {

const mutationKey = ['joinHunt'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof joinHunt>>, {slug: string;data: JoinHuntRequest}> = (props) => {
          const {slug,data} = props ?? {};

          return  joinHunt(slug,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type JoinHuntMutationResult = NonNullable<Awaited<ReturnType<typeof joinHunt>>>
    export type JoinHuntMutationBody = JoinHuntRequest
    export type JoinHuntMutationError = BadRequestResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Join a hunt with a nickname and optional team (rate-limited)
 */
export const useJoinHunt = <TError = BadRequestResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof joinHunt>>, TError,{slug: string;data: JoinHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof joinHunt>>,
        TError,
        {slug: string;data: JoinHuntRequest},
        TContext
      > => {

      const mutationOptions = getJoinHuntMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Get the caller's captures, completions, points, and rank
 */
export const getGetMyHuntProgressUrl = (slug: string,) => {


  

  return `/api/hunts/${slug}/me`
}

export const getMyHuntProgress = async (slug: string, options?: RequestInit): Promise<HuntProgress> => {
  
  return customFetch<HuntProgress>(getGetMyHuntProgressUrl(slug),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetMyHuntProgressQueryKey = (slug?: string,) => {
    return [
    `/api/hunts/${slug}/me`
    ] as const;
    }

    
export const getGetMyHuntProgressQueryOptions = <TData = Awaited<ReturnType<typeof getMyHuntProgress>>, TError = UnauthorizedResponse | NotFoundResponse | ServiceUnavailableResponse>(slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyHuntProgress>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyHuntProgressQueryKey(slug);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyHuntProgress>>> = ({ signal }) => getMyHuntProgress(slug, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(slug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyHuntProgress>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMyHuntProgressQueryResult = NonNullable<Awaited<ReturnType<typeof getMyHuntProgress>>>
export type GetMyHuntProgressQueryError = UnauthorizedResponse | NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get the caller's captures, completions, points, and rank
 */

export function useGetMyHuntProgress<TData = Awaited<ReturnType<typeof getMyHuntProgress>>, TError = UnauthorizedResponse | NotFoundResponse | ServiceUnavailableResponse>(
 slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyHuntProgress>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMyHuntProgressQueryOptions(slug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * QR capture: send `{ stationCode }` — the server resolves the station by
its secret code and records the capture.

GPS capture: send `{ stationId, latitude, longitude }` — the server
computes the haversine distance to the station coordinates and allows
capture only when within `captureRadiusM` + 15 m GPS slack.

 * @summary Capture a station via QR code or GPS proximity (rate-limited)
 */
export const getCaptureStationUrl = (slug: string,) => {


  

  return `/api/hunts/${slug}/capture`
}

export const captureStation = async (slug: string,
    captureRequest: CaptureRequest, options?: RequestInit): Promise<CaptureResult> => {
  
  return customFetch<CaptureResult>(getCaptureStationUrl(slug),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      captureRequest,)
  }
);}




export const getCaptureStationMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureStation>>, TError,{slug: string;data: CaptureRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof captureStation>>, TError,{slug: string;data: CaptureRequest}, TContext> => {

const mutationKey = ['captureStation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof captureStation>>, {slug: string;data: CaptureRequest}> = (props) => {
          const {slug,data} = props ?? {};

          return  captureStation(slug,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CaptureStationMutationResult = NonNullable<Awaited<ReturnType<typeof captureStation>>>
    export type CaptureStationMutationBody = CaptureRequest
    export type CaptureStationMutationError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse

    /**
 * @summary Capture a station via QR code or GPS proximity (rate-limited)
 */
export const useCaptureStation = <TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | RateLimitedResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureStation>>, TError,{slug: string;data: CaptureRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof captureStation>>,
        TError,
        {slug: string;data: CaptureRequest},
        TContext
      > => {

      const mutationOptions = getCaptureStationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Mark a challenge as completed (one completion per participant)
 */
export const getCompleteChallengeUrl = (slug: string,
    challengeId: string,) => {


  

  return `/api/hunts/${slug}/challenges/${challengeId}/complete`
}

export const completeChallenge = async (slug: string,
    challengeId: string,
    completeChallengeRequest?: CompleteChallengeRequest, options?: RequestInit): Promise<CompleteChallengeResult> => {
  
  return customFetch<CompleteChallengeResult>(getCompleteChallengeUrl(slug,challengeId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      completeChallengeRequest,)
  }
);}




export const getCompleteChallengeMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeChallenge>>, TError,{slug: string;challengeId: string;data: CompleteChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof completeChallenge>>, TError,{slug: string;challengeId: string;data: CompleteChallengeRequest}, TContext> => {

const mutationKey = ['completeChallenge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeChallenge>>, {slug: string;challengeId: string;data: CompleteChallengeRequest}> = (props) => {
          const {slug,challengeId,data} = props ?? {};

          return  completeChallenge(slug,challengeId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CompleteChallengeMutationResult = NonNullable<Awaited<ReturnType<typeof completeChallenge>>>
    export type CompleteChallengeMutationBody = CompleteChallengeRequest
    export type CompleteChallengeMutationError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | ServiceUnavailableResponse

    /**
 * @summary Mark a challenge as completed (one completion per participant)
 */
export const useCompleteChallenge = <TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse | Error | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeChallenge>>, TError,{slug: string;challengeId: string;data: CompleteChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof completeChallenge>>,
        TError,
        {slug: string;challengeId: string;data: CompleteChallengeRequest},
        TContext
      > => {

      const mutationOptions = getCompleteChallengeMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Ranked individuals and teams plus recent captures
 */
export const getGetHuntLeaderboardUrl = (slug: string,
    params?: GetHuntLeaderboardParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/hunts/${slug}/leaderboard?${stringifiedParams}` : `/api/hunts/${slug}/leaderboard`
}

export const getHuntLeaderboard = async (slug: string,
    params?: GetHuntLeaderboardParams, options?: RequestInit): Promise<HuntLeaderboard> => {
  
  return customFetch<HuntLeaderboard>(getGetHuntLeaderboardUrl(slug,params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetHuntLeaderboardQueryKey = (slug?: string,
    params?: GetHuntLeaderboardParams,) => {
    return [
    `/api/hunts/${slug}/leaderboard`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetHuntLeaderboardQueryOptions = <TData = Awaited<ReturnType<typeof getHuntLeaderboard>>, TError = NotFoundResponse | ServiceUnavailableResponse>(slug: string,
    params?: GetHuntLeaderboardParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHuntLeaderboard>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHuntLeaderboardQueryKey(slug,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHuntLeaderboard>>> = ({ signal }) => getHuntLeaderboard(slug,params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(slug), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHuntLeaderboard>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHuntLeaderboardQueryResult = NonNullable<Awaited<ReturnType<typeof getHuntLeaderboard>>>
export type GetHuntLeaderboardQueryError = NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Ranked individuals and teams plus recent captures
 */

export function useGetHuntLeaderboard<TData = Awaited<ReturnType<typeof getHuntLeaderboard>>, TError = NotFoundResponse | ServiceUnavailableResponse>(
 slug: string,
    params?: GetHuntLeaderboardParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHuntLeaderboard>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHuntLeaderboardQueryOptions(slug,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List all hunts (staff)
 */
export const getListHuntsUrl = () => {


  

  return `/api/workspace/hunts`
}

export const listHunts = async ( options?: RequestInit): Promise<HuntListResult> => {
  
  return customFetch<HuntListResult>(getListHuntsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListHuntsQueryKey = () => {
    return [
    `/api/workspace/hunts`
    ] as const;
    }

    
export const getListHuntsQueryOptions = <TData = Awaited<ReturnType<typeof listHunts>>, TError = UnauthorizedResponse | ForbiddenResponse | ServiceUnavailableResponse>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listHunts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListHuntsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listHunts>>> = ({ signal }) => listHunts({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listHunts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListHuntsQueryResult = NonNullable<Awaited<ReturnType<typeof listHunts>>>
export type ListHuntsQueryError = UnauthorizedResponse | ForbiddenResponse | ServiceUnavailableResponse


/**
 * @summary List all hunts (staff)
 */

export function useListHunts<TData = Awaited<ReturnType<typeof listHunts>>, TError = UnauthorizedResponse | ForbiddenResponse | ServiceUnavailableResponse>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listHunts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListHuntsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create a new hunt (staff)
 */
export const getCreateHuntUrl = () => {


  

  return `/api/workspace/hunts`
}

export const createHunt = async (createHuntRequest: CreateHuntRequest, options?: RequestInit): Promise<HuntDetail> => {
  
  return customFetch<HuntDetail>(getCreateHuntUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createHuntRequest,)
  }
);}




export const getCreateHuntMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | Error | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createHunt>>, TError,{data: CreateHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createHunt>>, TError,{data: CreateHuntRequest}, TContext> => {

const mutationKey = ['createHunt'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createHunt>>, {data: CreateHuntRequest}> = (props) => {
          const {data} = props ?? {};

          return  createHunt(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateHuntMutationResult = NonNullable<Awaited<ReturnType<typeof createHunt>>>
    export type CreateHuntMutationBody = CreateHuntRequest
    export type CreateHuntMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | Error | ServiceUnavailableResponse

    /**
 * @summary Create a new hunt (staff)
 */
export const useCreateHunt = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | Error | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createHunt>>, TError,{data: CreateHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createHunt>>,
        TError,
        {data: CreateHuntRequest},
        TContext
      > => {

      const mutationOptions = getCreateHuntMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Get a hunt with stations (including secret codes) and challenges
 */
export const getGetHuntDetailUrl = (huntId: string,) => {


  

  return `/api/workspace/hunts/${huntId}`
}

export const getHuntDetail = async (huntId: string, options?: RequestInit): Promise<HuntDetail> => {
  
  return customFetch<HuntDetail>(getGetHuntDetailUrl(huntId),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetHuntDetailQueryKey = (huntId?: string,) => {
    return [
    `/api/workspace/hunts/${huntId}`
    ] as const;
    }

    
export const getGetHuntDetailQueryOptions = <TData = Awaited<ReturnType<typeof getHuntDetail>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse>(huntId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHuntDetail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHuntDetailQueryKey(huntId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHuntDetail>>> = ({ signal }) => getHuntDetail(huntId, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(huntId), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHuntDetail>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHuntDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getHuntDetail>>>
export type GetHuntDetailQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse


/**
 * @summary Get a hunt with stations (including secret codes) and challenges
 */

export function useGetHuntDetail<TData = Awaited<ReturnType<typeof getHuntDetail>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse>(
 huntId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHuntDetail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHuntDetailQueryOptions(huntId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Update hunt details (staff)
 */
export const getUpdateHuntUrl = (huntId: string,) => {


  

  return `/api/workspace/hunts/${huntId}`
}

export const updateHunt = async (huntId: string,
    updateHuntRequest: UpdateHuntRequest, options?: RequestInit): Promise<HuntDetail> => {
  
  return customFetch<HuntDetail>(getUpdateHuntUrl(huntId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateHuntRequest,)
  }
);}




export const getUpdateHuntMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateHunt>>, TError,{huntId: string;data: UpdateHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateHunt>>, TError,{huntId: string;data: UpdateHuntRequest}, TContext> => {

const mutationKey = ['updateHunt'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateHunt>>, {huntId: string;data: UpdateHuntRequest}> = (props) => {
          const {huntId,data} = props ?? {};

          return  updateHunt(huntId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateHuntMutationResult = NonNullable<Awaited<ReturnType<typeof updateHunt>>>
    export type UpdateHuntMutationBody = UpdateHuntRequest
    export type UpdateHuntMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Update hunt details (staff)
 */
export const useUpdateHunt = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateHunt>>, TError,{huntId: string;data: UpdateHuntRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateHunt>>,
        TError,
        {huntId: string;data: UpdateHuntRequest},
        TContext
      > => {

      const mutationOptions = getUpdateHuntMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete a hunt and all related data (staff)
 */
export const getDeleteHuntUrl = (huntId: string,) => {


  

  return `/api/workspace/hunts/${huntId}`
}

export const deleteHunt = async (huntId: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteHuntUrl(huntId),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteHuntMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteHunt>>, TError,{huntId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteHunt>>, TError,{huntId: string}, TContext> => {

const mutationKey = ['deleteHunt'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteHunt>>, {huntId: string}> = (props) => {
          const {huntId} = props ?? {};

          return  deleteHunt(huntId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteHuntMutationResult = NonNullable<Awaited<ReturnType<typeof deleteHunt>>>
    
    export type DeleteHuntMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Delete a hunt and all related data (staff)
 */
export const useDeleteHunt = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteHunt>>, TError,{huntId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteHunt>>,
        TError,
        {huntId: string},
        TContext
      > => {

      const mutationOptions = getDeleteHuntMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Activate or close a hunt (staff)
 */
export const getSetHuntStatusUrl = (huntId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/status`
}

export const setHuntStatus = async (huntId: string,
    setHuntStatusRequest: SetHuntStatusRequest, options?: RequestInit): Promise<HuntDetail> => {
  
  return customFetch<HuntDetail>(getSetHuntStatusUrl(huntId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      setHuntStatusRequest,)
  }
);}




export const getSetHuntStatusMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setHuntStatus>>, TError,{huntId: string;data: SetHuntStatusRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setHuntStatus>>, TError,{huntId: string;data: SetHuntStatusRequest}, TContext> => {

const mutationKey = ['setHuntStatus'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setHuntStatus>>, {huntId: string;data: SetHuntStatusRequest}> = (props) => {
          const {huntId,data} = props ?? {};

          return  setHuntStatus(huntId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type SetHuntStatusMutationResult = NonNullable<Awaited<ReturnType<typeof setHuntStatus>>>
    export type SetHuntStatusMutationBody = SetHuntStatusRequest
    export type SetHuntStatusMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Activate or close a hunt (staff)
 */
export const useSetHuntStatus = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setHuntStatus>>, TError,{huntId: string;data: SetHuntStatusRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setHuntStatus>>,
        TError,
        {huntId: string;data: SetHuntStatusRequest},
        TContext
      > => {

      const mutationOptions = getSetHuntStatusMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Add a station to a hunt (staff); secret code generated server-side
 */
export const getCreateStationUrl = (huntId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations`
}

export const createStation = async (huntId: string,
    createStationRequest: CreateStationRequest, options?: RequestInit): Promise<StationDetail> => {
  
  return customFetch<StationDetail>(getCreateStationUrl(huntId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createStationRequest,)
  }
);}




export const getCreateStationMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStation>>, TError,{huntId: string;data: CreateStationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createStation>>, TError,{huntId: string;data: CreateStationRequest}, TContext> => {

const mutationKey = ['createStation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStation>>, {huntId: string;data: CreateStationRequest}> = (props) => {
          const {huntId,data} = props ?? {};

          return  createStation(huntId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateStationMutationResult = NonNullable<Awaited<ReturnType<typeof createStation>>>
    export type CreateStationMutationBody = CreateStationRequest
    export type CreateStationMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Add a station to a hunt (staff); secret code generated server-side
 */
export const useCreateStation = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStation>>, TError,{huntId: string;data: CreateStationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createStation>>,
        TError,
        {huntId: string;data: CreateStationRequest},
        TContext
      > => {

      const mutationOptions = getCreateStationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update a station (staff)
 */
export const getUpdateStationUrl = (huntId: string,
    stationId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations/${stationId}`
}

export const updateStation = async (huntId: string,
    stationId: string,
    updateStationRequest: UpdateStationRequest, options?: RequestInit): Promise<StationDetail> => {
  
  return customFetch<StationDetail>(getUpdateStationUrl(huntId,stationId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateStationRequest,)
  }
);}




export const getUpdateStationMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStation>>, TError,{huntId: string;stationId: string;data: UpdateStationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateStation>>, TError,{huntId: string;stationId: string;data: UpdateStationRequest}, TContext> => {

const mutationKey = ['updateStation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateStation>>, {huntId: string;stationId: string;data: UpdateStationRequest}> = (props) => {
          const {huntId,stationId,data} = props ?? {};

          return  updateStation(huntId,stationId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateStationMutationResult = NonNullable<Awaited<ReturnType<typeof updateStation>>>
    export type UpdateStationMutationBody = UpdateStationRequest
    export type UpdateStationMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Update a station (staff)
 */
export const useUpdateStation = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStation>>, TError,{huntId: string;stationId: string;data: UpdateStationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateStation>>,
        TError,
        {huntId: string;stationId: string;data: UpdateStationRequest},
        TContext
      > => {

      const mutationOptions = getUpdateStationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete a station (staff)
 */
export const getDeleteStationUrl = (huntId: string,
    stationId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations/${stationId}`
}

export const deleteStation = async (huntId: string,
    stationId: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteStationUrl(huntId,stationId),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteStationMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteStation>>, TError,{huntId: string;stationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteStation>>, TError,{huntId: string;stationId: string}, TContext> => {

const mutationKey = ['deleteStation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteStation>>, {huntId: string;stationId: string}> = (props) => {
          const {huntId,stationId} = props ?? {};

          return  deleteStation(huntId,stationId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteStationMutationResult = NonNullable<Awaited<ReturnType<typeof deleteStation>>>
    
    export type DeleteStationMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Delete a station (staff)
 */
export const useDeleteStation = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteStation>>, TError,{huntId: string;stationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteStation>>,
        TError,
        {huntId: string;stationId: string},
        TContext
      > => {

      const mutationOptions = getDeleteStationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Add a challenge to a station (staff)
 */
export const getCreateChallengeUrl = (huntId: string,
    stationId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations/${stationId}/challenges`
}

export const createChallenge = async (huntId: string,
    stationId: string,
    createChallengeRequest: CreateChallengeRequest, options?: RequestInit): Promise<ChallengeDetail> => {
  
  return customFetch<ChallengeDetail>(getCreateChallengeUrl(huntId,stationId),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      createChallengeRequest,)
  }
);}




export const getCreateChallengeMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createChallenge>>, TError,{huntId: string;stationId: string;data: CreateChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createChallenge>>, TError,{huntId: string;stationId: string;data: CreateChallengeRequest}, TContext> => {

const mutationKey = ['createChallenge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createChallenge>>, {huntId: string;stationId: string;data: CreateChallengeRequest}> = (props) => {
          const {huntId,stationId,data} = props ?? {};

          return  createChallenge(huntId,stationId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateChallengeMutationResult = NonNullable<Awaited<ReturnType<typeof createChallenge>>>
    export type CreateChallengeMutationBody = CreateChallengeRequest
    export type CreateChallengeMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Add a challenge to a station (staff)
 */
export const useCreateChallenge = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createChallenge>>, TError,{huntId: string;stationId: string;data: CreateChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createChallenge>>,
        TError,
        {huntId: string;stationId: string;data: CreateChallengeRequest},
        TContext
      > => {

      const mutationOptions = getCreateChallengeMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update a challenge (staff)
 */
export const getUpdateChallengeUrl = (huntId: string,
    stationId: string,
    challengeId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations/${stationId}/challenges/${challengeId}`
}

export const updateChallenge = async (huntId: string,
    stationId: string,
    challengeId: string,
    updateChallengeRequest: UpdateChallengeRequest, options?: RequestInit): Promise<ChallengeDetail> => {
  
  return customFetch<ChallengeDetail>(getUpdateChallengeUrl(huntId,stationId,challengeId),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateChallengeRequest,)
  }
);}




export const getUpdateChallengeMutationOptions = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateChallenge>>, TError,{huntId: string;stationId: string;challengeId: string;data: UpdateChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateChallenge>>, TError,{huntId: string;stationId: string;challengeId: string;data: UpdateChallengeRequest}, TContext> => {

const mutationKey = ['updateChallenge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateChallenge>>, {huntId: string;stationId: string;challengeId: string;data: UpdateChallengeRequest}> = (props) => {
          const {huntId,stationId,challengeId,data} = props ?? {};

          return  updateChallenge(huntId,stationId,challengeId,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateChallengeMutationResult = NonNullable<Awaited<ReturnType<typeof updateChallenge>>>
    export type UpdateChallengeMutationBody = UpdateChallengeRequest
    export type UpdateChallengeMutationError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Update a challenge (staff)
 */
export const useUpdateChallenge = <TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateChallenge>>, TError,{huntId: string;stationId: string;challengeId: string;data: UpdateChallengeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateChallenge>>,
        TError,
        {huntId: string;stationId: string;challengeId: string;data: UpdateChallengeRequest},
        TContext
      > => {

      const mutationOptions = getUpdateChallengeMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete a challenge (staff)
 */
export const getDeleteChallengeUrl = (huntId: string,
    stationId: string,
    challengeId: string,) => {


  

  return `/api/workspace/hunts/${huntId}/stations/${stationId}/challenges/${challengeId}`
}

export const deleteChallenge = async (huntId: string,
    stationId: string,
    challengeId: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteChallengeUrl(huntId,stationId,challengeId),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteChallengeMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteChallenge>>, TError,{huntId: string;stationId: string;challengeId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteChallenge>>, TError,{huntId: string;stationId: string;challengeId: string}, TContext> => {

const mutationKey = ['deleteChallenge'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteChallenge>>, {huntId: string;stationId: string;challengeId: string}> = (props) => {
          const {huntId,stationId,challengeId} = props ?? {};

          return  deleteChallenge(huntId,stationId,challengeId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteChallengeMutationResult = NonNullable<Awaited<ReturnType<typeof deleteChallenge>>>
    
    export type DeleteChallengeMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse

    /**
 * @summary Delete a challenge (staff)
 */
export const useDeleteChallenge = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ServiceUnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteChallenge>>, TError,{huntId: string;stationId: string;challengeId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteChallenge>>,
        TError,
        {huntId: string;stationId: string;challengeId: string},
        TContext
      > => {

      const mutationOptions = getDeleteChallengeMutationOptions(options);

      return useMutation(mutationOptions);
    }
